<template>
  <div :style="styleVars" class="min-h-screen flex flex-col bg-white">
    <!-- SEO Meta Tags and Schema -->
    <div v-if="!isLoading && storeLocation" class="seo-meta-container">
      <!-- Page Title -->
      <title>{{ seoPageTitle }}</title>

      <!-- Meta Description -->
      <meta name="description" :content="seoMetaDescription" />

      <!-- Keywords -->
      <meta name="keywords" :content="seoKeywords" />

      <!-- Open Graph Tags -->
      <meta property="og:title" :content="seoPageTitle" />
      <meta property="og:description" :content="seoMetaDescription" />
      <meta property="og:type" content="website" />
      <meta property="og:url" :content="currentPageUrl" />
      <meta property="og:image" :content="seoImageUrl" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" :content="brandName" />
      <meta property="og:locale" content="en_US" />

      <!-- Twitter Card Tags -->
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" :content="seoPageTitle" />
      <meta name="twitter:description" :content="seoMetaDescription" />
      <meta name="twitter:image" :content="seoImageUrl" />
      <meta name="twitter:site" content="@flysocial" />

      <!-- Additional Meta Tags -->
      <meta name="robots" content="index, follow" />
      <meta name="author" :content="brandName" />
      <meta name="geo.region" :content="seoGeoRegion" />
      <meta name="geo.placename" :content="seoGeoPlaceName" />
      <meta name="geo.position" :content="seoGeoPosition" />
      <meta name="ICBM" :content="seoGeoPosition" />

      <!-- Canonical URL -->
      <link rel="canonical" :href="currentPageUrl" />

      <!-- Schema.org Structured Data -->
      <script type="application/ld+json" v-html="schemaMarkup"></script>
    </div>
    <!-- Loading Animation -->
    <div v-if="isLoading" class="min-h-screen flex flex-col bg-white">
      <!-- Header Skeleton -->
      <header
        class="w-full shadow-lg border-b border-gray-100 py-4 px-6 flex items-center justify-between z-50 bg-white"
      >
        <div class="flex items-center gap-4">
          <div class="relative">
            <div
              class="bg-gray-200 rounded-lg animate-pulse"
              style="height: 3rem; width: 3rem"
            ></div>
          </div>
          <div class="flex flex-col gap-2">
            <div
              class="h-6 bg-gray-200 rounded animate-pulse"
              style="width: 150px"
            ></div>
            <div
              class="h-4 bg-gray-200 rounded animate-pulse"
              style="width: 100px"
            ></div>
          </div>
        </div>
        <div class="hidden md:flex">
          <div
            class="h-10 bg-gray-200 rounded-lg animate-pulse"
            style="width: 140px"
          ></div>
        </div>
      </header>

      <!-- Hero Section Skeleton -->
      <section
        class="relative py-20 px-6"
        :style="{ background: 'var(--hero-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="flex flex-col items-center text-center max-w-6xl mx-auto">
            <div class="space-y-8 mb-12 w-full">
              <div class="space-y-4">
                <div
                  class="h-16 bg-gray-200 rounded animate-pulse mx-auto"
                  style="width: 300px"
                ></div>
                <div
                  class="h-6 bg-gray-200 rounded animate-pulse mx-auto"
                  style="width: 80%; max-width: 600px"
                ></div>
                <div
                  class="h-6 bg-gray-200 rounded animate-pulse mx-auto"
                  style="width: 60%; max-width: 500px"
                ></div>
              </div>
              <div class="flex flex-col sm:flex-row gap-4 justify-center">
                <div
                  class="h-12 bg-gray-200 rounded-lg animate-pulse"
                  style="width: 180px"
                ></div>
                <div
                  class="h-12 bg-gray-200 rounded-lg animate-pulse"
                  style="width: 120px"
                ></div>
              </div>
            </div>
            <div class="w-full max-w-6xl">
              <div class="h-96 bg-gray-200 rounded-2xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- Reviews Section Skeleton -->
      <section
        class="py-16 px-6"
        :style="{ backgroundColor: 'var(--reviews-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <div
              class="h-10 bg-gray-200 rounded animate-pulse mx-auto mb-4"
              style="width: 250px"
            ></div>
            <div
              class="h-6 bg-gray-200 rounded animate-pulse mx-auto"
              style="width: 350px"
            ></div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              v-for="i in 3"
              :key="i"
              :style="{
                backgroundColor: 'var(--review-card-bg-color)',
                borderRadius: 'var(--review-card-border-radius)',
                border:
                  'var(--review-card-border-size) solid var(--store-border-color)',
              }"
              class="p-8 shadow-lg"
            >
              <div class="flex items-center gap-3 mb-4">
                <div
                  class="w-12 h-12 bg-gray-200 rounded-full animate-pulse"
                ></div>
                <div class="flex-1">
                  <div
                    class="h-4 bg-gray-200 rounded animate-pulse mb-2"
                    style="width: 70%"
                  ></div>
                  <div class="flex gap-1">
                    <div
                      v-for="star in 5"
                      :key="star"
                      class="w-4 h-4 bg-gray-200 rounded animate-pulse"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="space-y-2">
                <div
                  class="h-4 bg-gray-200 rounded animate-pulse"
                  style="width: 100%"
                ></div>
                <div
                  class="h-4 bg-gray-200 rounded animate-pulse"
                  style="width: 80%"
                ></div>
                <div
                  class="h-4 bg-gray-200 rounded animate-pulse"
                  style="width: 60%"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section Skeleton -->
      <section class="py-12 px-6 bg-white">
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-8">
            <div
              class="h-10 bg-gray-200 rounded animate-pulse mx-auto mb-4"
              style="width: 220px"
            ></div>
            <div
              class="h-6 bg-gray-200 rounded animate-pulse mx-auto"
              style="width: 400px"
            ></div>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="space-y-4">
              <div class="bg-gray-50 rounded-xl p-6">
                <div
                  class="h-6 bg-gray-200 rounded animate-pulse mb-4"
                  style="width: 120px"
                ></div>
                <div class="space-y-4">
                  <div v-for="i in 4" :key="i" class="flex items-start gap-3">
                    <div
                      class="w-8 h-8 bg-gray-200 rounded-full animate-pulse"
                    ></div>
                    <div class="flex-1">
                      <div
                        class="h-4 bg-gray-200 rounded animate-pulse mb-2"
                        style="width: 60px"
                      ></div>
                      <div
                        class="h-4 bg-gray-200 rounded animate-pulse"
                        style="width: 80%"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-gray-50 rounded-xl p-4">
              <div
                class="h-6 bg-gray-200 rounded animate-pulse mb-3"
                style="width: 150px"
              ></div>
              <div class="h-80 bg-gray-200 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      <!-- Footer Skeleton -->
      <footer 
        class="text-white py-12 px-6"
        :style="{
          backgroundColor: 'var(--footer-bg-color)',
          color: 'var(--footer-text-color)'
        }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div class="flex items-center gap-3 mb-4">
                <div 
                  class="h-10 w-10 rounded animate-pulse"
                  :style="{ backgroundColor: 'var(--footer-skeleton-color)' }"
                ></div>
                <div
                  class="h-6 rounded animate-pulse"
                  :style="{ 
                    width: '120px',
                    backgroundColor: 'var(--footer-skeleton-color)'
                  }"
                ></div>
              </div>
            </div>
            <div>
              <div
                class="h-6 rounded animate-pulse mb-4"
                :style="{ 
                  width: '100px',
                  backgroundColor: 'var(--footer-skeleton-color)'
                }"
              ></div>
              <div class="space-y-2">
                <div
                  v-for="i in 3"
                  :key="i"
                  class="h-4 rounded animate-pulse"
                  :style="{ 
                    width: '80%',
                    backgroundColor: 'var(--footer-skeleton-color)'
                  }"
                ></div>
              </div>
            </div>
            <div>
              <div
                class="h-6 rounded animate-pulse mb-4"
                :style="{ 
                  width: '100px',
                  backgroundColor: 'var(--footer-skeleton-color)'
                }"
              ></div>
              <div class="space-y-2">
                <div
                  v-for="i in 3"
                  :key="i"
                  class="h-4 rounded animate-pulse"
                  :style="{ 
                    width: '70%',
                    backgroundColor: 'var(--footer-skeleton-color)'
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>

    <!-- Loading State (shown when still loading) -->
    <div v-else-if="isLoading" class="min-h-screen flex flex-col bg-white">
      <div class="flex-1 flex items-center justify-center px-6">
        <div class="text-center max-w-md">
          <div
            class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <v-icon size="48" color="blue" class="animate-spin"
              >mdi-loading</v-icon
            >
          </div>
          <h1 class="text-2xl font-bold text-gray-800 mb-4">
            Loading Store...
          </h1>
          <p class="text-gray-600 mb-6">
            Please wait while we fetch the store information.
          </p>
        </div>
      </div>
    </div>

    <!-- Actual Content (shown when not loading) -->
    <div v-else>
      <!-- Header -->
      <header
        class="w-full shadow-lg border-b border-gray-100 py-4 px-6 flex items-center justify-between z-50"
        :style="{ backgroundColor: 'var(--header-background-color)' }"
      >
        <div class="flex items-center gap-4">
          <div 
            class="flex items-center gap-4 cursor-pointer"
            @click="navigateToSection('home')"
            role="button"
            tabindex="0"
            @keydown.enter="navigateToSection('home')"
            @keydown.space="navigateToSection('home')"
          >
            <div class="relative">
              <img
                v-if="storeLogo"
                :src="storeLogo"
                :alt="brandName"
                class="object-contain rounded-lg shadow-sm"
                :style="{ height: 'var(--header-logo-size)', width: 'auto' }"
                @error="logoLoadError = true"
              />
              <div
                v-else
                class="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-sm flex items-center justify-center"
                :style="{
                  height: 'var(--header-logo-size)',
                  width: 'var(--header-logo-size)',
                }"
              >
                <v-icon
                  color="white"
                  :size="parseInt(headerStyles.logoSize || '48') / 2"
                  >mdi-store</v-icon
                >
              </div>
            </div>
            <div class="flex flex-col">
              <span
                class="font-bold"
                :style="{
                  fontSize: 'var(--header-title-size)',
                  color: 'var(--header-text-color)',
                }"
                >{{ brandName }}</span
              >
              <span
                v-if="storeLocation?.city || storeLocation?.state"
                class="text-sm"
                :style="{ color: 'var(--header-text-color)' }"
                >{{ storeLocation?.city
                }}{{ storeLocation?.city && storeLocation?.state ? ", " : ""
                }}{{ storeLocation?.state }}</span
              >
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="hidden md:flex items-center gap-6">
          <!-- Navigation Tabs -->
          <nav class="flex items-center gap-6">
            <a
              href="#home"
              @click.prevent="navigateToSection('home')"
              class="text-sm font-medium cursor-pointer"
              :style="{ color: 'var(--header-text-color)' }"
            >
              Home
            </a>
            <a
              v-if="showContactSection"
              href="#contact"
              @click.prevent="navigateToSection('contact')"
              class="text-sm font-medium cursor-pointer"
              :style="{ color: 'var(--header-text-color)' }"
            >
              Contact
            </a>
            <a
              v-if="showReviewsSection"
              href="#reviews"
              @click.prevent="navigateToSection('reviews')"
              class="text-sm font-medium cursor-pointer"
              :style="{ color: 'var(--header-text-color)' }"
            >
              Reviews
            </a>
            <a
              v-if="showPostsSection"
              href="#posts"
              @click.prevent="navigateToSection('posts')"
              class="text-sm font-medium cursor-pointer"
              :style="{ color: 'var(--header-text-color)' }"
            >
              Posts
            </a>
            <a
              v-if="showProductsSection && availableServices.length > 0"
              href="#products"
              @click.prevent="navigateToSection('products')"
              class="text-sm font-medium cursor-pointer"
              :style="{ color: 'var(--header-text-color)' }"
            >
              {{ getProductsServicesSectionName() }}
            </a>
            <a
              v-if="showSocialMediaSection && socialMediaLinks.length > 0"
              href="#social-media"
              @click.prevent="navigateToSection('social-media')"
              class="text-sm font-medium cursor-pointer"
              :style="{ color: 'var(--header-text-color)' }"
            >
              Social Media
            </a>
            <a
              v-if="showBlogsSection && blogs.length > 0"
              href="#blogs"
              @click.prevent="navigateToSection('blogs')"
              class="text-sm font-medium cursor-pointer"
              :style="{ color: 'var(--header-text-color)' }"
            >
              Blog
            </a>
            <a
              v-if="showGallerySection && galleryImages.length > 0"
              href="#gallery"
              @click.prevent="navigateToSection('gallery')"
              class="text-sm font-medium cursor-pointer"
              :style="{ color: 'var(--header-text-color)' }"
            >
              Gallery
            </a>
          </nav>

          <!-- Appointment Button -->
          <button
            v-if="appointments && showAppointmentButton"
            @click="scrollToAppointment"
            class="px-6 py-2 font-semibold appointment-button"
          >
            {{ appointmentButtonText }}
          </button>
          <!-- Call Now Button (if phone is available) -->
          <button
            v-if="getGmbPhone(storeLocation) && showCallButton"
            @click="callBusiness"
            class="px-6 py-2 font-semibold appointment-button"
          >
            {{ callButtonText }}
          </button>
        </div>
      </header>

      <!-- Hero Section -->
      <section
        id="home"
        v-if="
          showHeroSection &&
          (currentSection === 'home' || currentSection === '')
        "
        class="relative py-20 px-6"
        :style="{ background: 'var(--hero-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="flex flex-col items-center text-center max-w-6xl mx-auto">
            <!-- Cover Photo at the very top -->
            <div
              v-if="getCoverPhoto(storeLocation) && !coverPhotoError"
              class="w-full mb-8"
            >
              <div class="cover-photo-container">
                <div
                  v-if="coverPhotoLoading"
                  class="w-full h-64 bg-gray-200 rounded-lg shadow-md flex items-center justify-center"
                  :style="{
                    height: 'var(--hero-cover-photo-height)',
                    borderRadius: 'var(--hero-cover-photo-border-radius)',
                  }"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="32"
                  ></v-progress-circular>
                  <span class="ml-2 text-gray-500">Loading cover photo...</span>
                </div>
                <img
                  v-show="!coverPhotoLoading"
                  :src="getCoverPhoto(storeLocation)"
                  :alt="`Cover photo for ${
                    storeLocation?.name || storeLocation?.storeLocation
                  }`"
                  class="w-full object-cover rounded-lg shadow-md"
                  :style="{
                    height: 'var(--hero-cover-photo-height)',
                    borderRadius: 'var(--hero-cover-photo-border-radius)',
                  }"
                  @error="handleCoverPhotoError"
                  @load="handleCoverPhotoLoad"
                  @loadstart="coverPhotoLoading = true"
                />
              </div>
            </div>

            <!-- Brand Name -->
            <h1
              class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight"
              :style="{
                fontSize: 'var(--hero-brand-name-size)',
                color: 'var(--hero-brand-name-color)',
              }"
            >
              {{ brandName }}
            </h1>

            <!-- Rating and Review Count (centered, single row) -->
            <div
              v-if="storeLocation?.averageRating || storeLocation?.reviewCount"
              class="flex items-center gap-2 mb-4 mt-2"
              style="justify-content: center"
            >
              <!-- Star Rating -->
              <div class="flex items-center">
                <v-icon
                  v-for="star in 5"
                  :key="star"
                  :color="
                    star <= Math.round(storeLocation?.averageRating || 0)
                      ? 'amber'
                      : 'grey-lighten-2'
                  "
                  size="24"
                >
                  {{
                    star <= Math.round(storeLocation?.averageRating || 0)
                      ? "mdi-star"
                      : "mdi-star-outline"
                  }}
                </v-icon>
              </div>
              <!-- Numeric Rating -->
              <span
                v-if="storeLocation?.averageRating"
                class="text-lg font-semibold text-gray-700 ml-2"
              >
                {{ storeLocation.averageRating.toFixed(1) }}
              </span>
              <!-- Dot Separator -->
              <span
                v-if="
                  storeLocation?.averageRating && storeLocation?.reviewCount
                "
                class="text-lg text-gray-600 mx-1"
                >•</span
              >
              <!-- Review Count -->
              <span
                v-if="storeLocation?.reviewCount"
                class="text-lg text-gray-600"
              >
                {{ storeLocation.reviewCount }}
                {{ storeLocation.reviewCount === 1 ? "review" : "reviews" }}
              </span>
            </div>

            <!-- Description -->
            <p
              class="text-xl text-gray-600 leading-relaxed"
              :style="{
                fontSize: 'var(--hero-description-size)',
                color: 'var(--hero-description-color)',
              }"
            >
              {{
                getBusinessDescription(storeLocation) ||
                `Welcome to ${brandName}, where quality service meets excellence. Our expert team is committed to providing you with the best experience in a comfortable, welcoming environment.`
              }}
            </p>
          </div>
        </div>
      </section>

      <!-- Products & Services Section -->
      <section
        id="products"
        v-if="
          showProductsSection &&
          availableServices.length > 0 &&
          (currentSection === 'products' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        class="py-16 px-6"
        :style="{ backgroundColor: 'var(--product-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <h2 
              class="text-3xl md:text-4xl font-bold mb-4"
              :style="{ color: 'var(--primary-title-color)' }"
            >
              {{ getProductsSectionTitle() }}
            </h2>
            <p class="text-lg text-gray-600">
              {{ getProductsSectionSubtext() }}
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              v-for="(service, index) in displayedServices"
              :key="index"
              :style="{
                backgroundColor: 'var(--product-card-bg-color)',
                borderRadius: 'var(--product-card-border-radius)',
                border:
                  'var(--product-card-border-size) solid var(--product-card-border-color)',
              }"
              class="p-8 shadow-lg cursor-pointer"
              @click="openProductDetail(service, index)"
            >
              <!-- Media Display (Images or Videos) -->
              <div
                v-if="getServiceImage(service) || getServiceVideo(service)"
                class="w-full h-48 mb-6 overflow-hidden relative"
                :style="{ borderRadius: 'var(--product-image-border-radius)' }"
              >
                <!-- Image Display -->
                <img
                  v-if="getServiceImage(service)"
                  :src="getServiceImage(service)"
                  :alt="service.name"
                  class="w-full h-full object-cover"
                  :style="{
                    borderRadius: 'var(--product-image-border-radius)',
                  }"
                />
                
                <!-- Video Thumbnail Display -->
                <div
                  v-else-if="getServiceVideo(service)"
                  class="w-full h-full bg-gray-200 flex items-center justify-center relative group cursor-pointer"
                  :style="{
                    borderRadius: 'var(--product-image-border-radius)',
                  }"
                  @click.stop="playServiceVideo(service)"
                >
                  <!-- Video Thumbnail -->
                  <img
                    v-if="getServiceVideoThumbnail(service)"
                    :src="getServiceVideoThumbnail(service)"
                    :alt="`${service.name} video thumbnail`"
                    class="w-full h-full object-cover"
                    :style="{
                      borderRadius: 'var(--product-image-border-radius)',
                    }"
                  />
                  
                  <!-- Play Button Overlay -->
                  <div class="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-40 transition-all duration-300">
                    <div class="bg-white bg-opacity-90 rounded-full p-3 group-hover:bg-opacity-100 transition-all duration-300">
                      <v-icon color="blue" size="32">mdi-play</v-icon>
                    </div>
                  </div>
                  
                  <!-- Video Duration Badge -->
                  <div class="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                    Video
                  </div>
                </div>
              </div>
              
              <!-- Default Icon when no media -->
              <div
                v-else
                class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6"
              >
                <v-icon color="blue" size="32">mdi-cog</v-icon>
              </div>
              <h3 class="text-xl font-bold text-gray-800 mb-4">
                {{ service.name }}
              </h3>
              <p
                v-if="service.description"
                class="text-gray-600 mb-4 leading-relaxed"
              >
                {{ truncateDescription(service.description) }}
              </p>
              <div
                v-if="getServicePrice(service)"
                class="text-lg font-semibold text-blue-600"
              >
                {{ getServicePrice(service) }}
              </div>
            </div>
          </div>

          <!-- Show More Button -->
          <div v-if="availableServices.length > 6 && currentSection !== 'products'" class="text-center mt-8">
            <button
              @click="navigateToProducts"
              class="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 font-medium transition-colors duration-200"
            >
              Show All {{ availableServices.length }} Products & Services
            </button>
          </div>
        </div>
      </section>

      <!-- Reviews Section -->
      <section
        id="reviews"
        v-if="
          showReviewsSection &&
          reviews.length > 0 &&
          (currentSection === 'reviews' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        class="py-16 px-6"
        :style="{ backgroundColor: 'var(--reviews-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <h2 
              class="text-3xl md:text-4xl font-bold mb-4"
              :style="{ color: 'var(--primary-title-color)' }"
            >
              {{ getReviewsSectionTitle() }}
            </h2>
            <p class="text-lg text-gray-600">
              {{ getReviewsSectionSubtext() }}
            </p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              v-for="review in reviews.slice(0, 6)"
              :key="review.reviewId"
              :style="{
                backgroundColor: 'var(--review-card-bg-color)',
                borderRadius: 'var(--review-card-border-radius)',
                border:
                  'var(--review-card-border-size) solid var(--store-border-color)',
                color: 'var(--review-card-text-color)',
              }"
              class="p-8 shadow-lg"
            >
              <div class="flex items-center gap-3 mb-4">
                <img
                  :src="review.profilePhotoUrl"
                  alt="profile"
                  class="w-12 h-12 rounded-full object-cover"
                  @error="
                    (event) =>
                      (event.target.src =
                        'https://www.gravatar.com/avatar/?d=mp')
                  "
                />
                <div>
                  <p
                    class="font-semibold"
                    :style="{ color: 'var(--review-card-text-color)' }"
                  >
                    {{ review.displayName }}
                  </p>
                  <div class="flex">
                    <v-icon
                      v-for="star in 5"
                      :key="star"
                      :color="
                        star <= review.starRating ? 'amber' : 'grey-lighten-2'
                      "
                      size="16"
                    >
                      {{
                        star <= review.starRating
                          ? "mdi-star"
                          : "mdi-star-outline"
                      }}
                    </v-icon>
                  </div>
                </div>
              </div>
              <p
                class="italic"
                :style="{ color: 'var(--review-card-text-color)' }"
              >
                "{{ review.comment }}"
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Appointment Section -->
      <section
        v-if="
          showAppointmentSection &&
          appointments &&
          (currentSection === 'appointment' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        id="appointment"
        class="py-16 px-6"
        :style="{ backgroundColor: 'var(--appointment-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <h2 
              class="text-3xl md:text-4xl font-bold mb-4"
              :style="{ color: 'var(--appointment-title-color)' }"
            >
              {{ getAppointmentSectionTitle() }}
            </h2>
            <p 
              class="text-lg"
              :style="{ color: 'var(--appointment-description-color)' }"
            >
              {{ getAppointmentSectionSubtext() }}
            </p>
          </div>

          <div
            class="p-8 shadow-lg max-w-2xl mx-auto"
            :style="{ 
              borderRadius: 'var(--appointment-form-border-radius)',
              backgroundColor: 'var(--appointment-form-bg-color)'
            }"
          >
            <h3 
              class="text-2xl font-bold mb-6"
              :style="{ color: 'var(--appointment-form-title-color)' }"
            >
              Schedule Your Visit
            </h3>
            <form @submit.prevent="submitAppointment" class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label 
                    class="block text-sm font-semibold mb-2"
                    :style="{ color: 'var(--appointment-label-color)' }"
                  >
                    First Name *
                  </label>
                  <input
                    v-model="appointmentForm.firstName"
                    type="text"
                    required
                    :class="[
                      'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors',
                      appointmentForm.firstName
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300',
                    ]"
                    placeholder="Enter your first name"
                    @input="validateField('firstName')"
                  />
                  <p
                    v-if="validationErrors.firstName"
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ validationErrors.firstName }}
                  </p>
                </div>
                <div>
                  <label 
                    class="block text-sm font-semibold mb-2"
                    :style="{ color: 'var(--appointment-label-color)' }"
                  >
                    Last Name *
                  </label>
                  <input
                    v-model="appointmentForm.lastName"
                    type="text"
                    required
                    :class="[
                      'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors',
                      appointmentForm.lastName
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300',
                    ]"
                    placeholder="Enter your last name"
                    @input="validateField('lastName')"
                  />
                  <p
                    v-if="validationErrors.lastName"
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ validationErrors.lastName }}
                  </p>
                </div>
              </div>

              <div>
                <label 
                  class="block text-sm font-semibold mb-2"
                  :style="{ color: 'var(--appointment-label-color)' }"
                >
                  Email
                </label>
                <input
                  v-model="appointmentForm.email"
                  type="email"
                  :class="[
                    'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors',
                    appointmentForm.email && isValidEmail(appointmentForm.email)
                      ? 'border-green-300 bg-green-50'
                      : appointmentForm.email &&
                        !isValidEmail(appointmentForm.email)
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300',
                  ]"
                  placeholder="Enter your email address"
                  @input="validateField('email')"
                />
                <p
                  v-if="validationErrors.email"
                  class="text-red-500 text-xs mt-1"
                >
                  {{ validationErrors.email }}
                </p>
              </div>

              <div>
                <label 
                  class="block text-sm font-semibold mb-2"
                  :style="{ color: 'var(--appointment-label-color)' }"
                >
                  Phone *
                </label>
                <input
                  v-model="appointmentForm.phone"
                  type="tel"
                  required
                  :class="[
                    'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors',
                    appointmentForm.phone
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-300',
                  ]"
                  placeholder="Enter your phone number"
                  @input="validateField('phone')"
                />
                <p
                  v-if="validationErrors.phone"
                  class="text-red-500 text-xs mt-1"
                >
                  {{ validationErrors.phone }}
                </p>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label 
                    class="block text-sm font-semibold mb-2"
                    :style="{ color: 'var(--appointment-label-color)' }"
                  >
                    Preferred Date *
                  </label>
                  <input
                    v-model="appointmentForm.date"
                    type="date"
                    required
                    :min="new Date().toISOString().split('T')[0]"
                    :class="[
                      'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors',
                      appointmentForm.date
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300',
                    ]"
                    @change="validateField('date')"
                  />
                  <p
                    v-if="validationErrors.date"
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ validationErrors.date }}
                  </p>
                </div>
                <div>
                  <label 
                    class="block text-sm font-semibold mb-2"
                    :style="{ color: 'var(--appointment-label-color)' }"
                  >
                    Preferred Time *
                  </label>
                  <select
                    v-model="appointmentForm.time"
                    required
                    :class="[
                      'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors',
                      appointmentForm.time
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-300',
                    ]"
                    @change="validateField('time')"
                  >
                    <option value="">Select a time</option>
                    <option value="09:00">9:00 AM</option>
                    <option value="10:00">10:00 AM</option>
                    <option value="11:00">11:00 AM</option>
                    <option value="12:00">12:00 PM</option>
                    <option value="13:00">1:00 PM</option>
                    <option value="14:00">2:00 PM</option>
                    <option value="15:00">3:00 PM</option>
                    <option value="16:00">4:00 PM</option>
                    <option value="17:00">5:00 PM</option>
                  </select>
                  <p
                    v-if="validationErrors.time"
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ validationErrors.time }}
                  </p>
                </div>
              </div>

              <div>
                <label 
                  class="block text-sm font-semibold mb-2"
                  :style="{ color: 'var(--appointment-label-color)' }"
                >
                  Message (Optional)
                </label>
                <textarea
                  v-model="appointmentForm.message"
                  rows="4"
                  placeholder="Tell us about your needs, special requirements, or any questions you have..."
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  maxlength="500"
                ></textarea>
                <div class="flex justify-end items-center mt-1">
                  <p class="text-xs text-gray-500">
                    {{ appointmentForm.message.length }}/500
                  </p>
                </div>
              </div>

              <button
                type="submit"
                :disabled="isSubmitting || !isFormValid"
                class="w-full py-4 font-bold disabled:opacity-50 disabled:cursor-not-allowed appointment-button transition-all duration-200"
              >
                <div
                  v-if="isSubmitting"
                  class="flex items-center justify-center gap-2"
                >
                  <v-icon size="20" class="animate-spin">mdi-loading</v-icon>
                  <span>Submitting...</span>
                </div>
                <div v-else class="flex items-center justify-center gap-2">
                  <v-icon size="20">mdi-calendar-check</v-icon>
                  <span>Book Appointment</span>
                </div>
              </button>
            </form>
          </div>
        </div>
      </section>

      <!-- Enable Appointments Section -->
      <section
        v-if="
          showAppointmentSection &&
          !appointments &&
          (currentSection === 'appointment' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        id="enable-appointments"
        class="py-16 px-6 bg-gray-50"
      >
        <div class="max-w-4xl mx-auto">
          <div class="text-center mb-12">
            <div
              class="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <v-icon color="blue" size="32">mdi-calendar-clock</v-icon>
            </div>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Enable Appointment Booking
            </h2>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              Allow your customers to book appointments directly through your
              store page. This feature will add an appointment booking form and
              button to your store.
            </p>
          </div>

          <div class="bg-white p-8 shadow-lg max-w-2xl mx-auto rounded-xl">
            <div class="text-center">
              <h3 class="text-2xl font-bold text-gray-800 mb-4">
                Benefits of Appointment Booking
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div class="text-left">
                  <div class="flex items-center gap-3 mb-2">
                    <v-icon color="green" size="20">mdi-check-circle</v-icon>
                    <span class="font-semibold text-gray-800"
                      >Reduce Phone Calls</span
                    >
                  </div>
                  <p class="text-sm text-gray-600 ml-8">
                    Let customers book online 24/7
                  </p>
                </div>
                <div class="text-left">
                  <div class="flex items-center gap-3 mb-2">
                    <v-icon color="green" size="20">mdi-check-circle</v-icon>
                    <span class="font-semibold text-gray-800"
                      >Better Scheduling</span
                    >
                  </div>
                  <p class="text-sm text-gray-600 ml-8">
                    Organize appointments efficiently
                  </p>
                </div>
                <div class="text-left">
                  <div class="flex items-center gap-3 mb-2">
                    <v-icon color="green" size="20">mdi-check-circle</v-icon>
                    <span class="font-semibold text-gray-800"
                      >Customer Convenience</span
                    >
                  </div>
                  <p class="text-sm text-gray-600 ml-8">
                    Easy booking experience
                  </p>
                </div>
                <div class="text-left">
                  <div class="flex items-center gap-3 mb-2">
                    <v-icon color="green" size="20">mdi-check-circle</v-icon>
                    <span class="font-semibold text-gray-800"
                      >Lead Generation</span
                    >
                  </div>
                  <p class="text-sm text-gray-600 ml-8">
                    Capture customer information
                  </p>
                </div>
              </div>

              <button
                @click="enableAppointments"
                :disabled="isEnablingAppointments"
                class="px-8 py-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2 mx-auto"
              >
                <v-icon
                  v-if="isEnablingAppointments"
                  size="20"
                  class="animate-spin"
                  >mdi-loading</v-icon
                >
                <v-icon v-else size="20">mdi-calendar-plus</v-icon>
                <span v-if="isEnablingAppointments">Enabling...</span>
                <span v-else>Enable Appointment Booking</span>
              </button>

              <p class="text-sm text-gray-500 mt-4">
                This will add an appointment booking form to your store page
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Us Section -->
      <section
        id="contact"
        v-if="
          showContactSection &&
          (currentSection === 'contact' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        class="contact-section py-12 px-6"
        :style="{ backgroundColor: 'var(--contact-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-8">
            <h2 class="text-3xl md:text-4xl font-bold mb-4" :style="{ color: 'var(--primary-title-color)' }">
              {{ getContactSectionTitle() }}
            </h2>
            <p class="text-lg max-w-2xl mx-auto" :style="{ color: 'var(--contact-description-color)' }">
              {{ getContactSectionSubtext() }}
            </p>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Left Column: Contact Details & Business Hours -->
            <div class="space-y-4">
              <!-- Contact Details -->
              <div 
                class="rounded-xl p-6"
                :style="{
                  backgroundColor: 'var(--contact-card-bg-color)',
                  borderRadius: 'var(--contact-card-border-radius)',
                  border: '1px solid var(--contact-card-border-color)'
                }"
              >
                <h3 
                  class="text-xl font-bold mb-4"
                  :style="{ color: 'var(--primary-title-color)' }"
                >
                  Get In Touch
                </h3>

                <div class="space-y-4">
                  <!-- Phone -->
                  <div
                    v-if="getGmbPhone(storeLocation)"
                    class="flex items-start gap-3"
                  >
                    <div>
                      <h4 class="text-base font-semibold mb-1" :style="{ color: 'var(--contact-label-color)' }">
                        Phone
                      </h4>
                      <a
                        :href="`tel:${getGmbPhone(storeLocation)}`"
                        class="text-base font-medium hover:underline"
                        :style="{ color: 'var(--contact-link-color)' }"
                      >
                        {{ getGmbPhone(storeLocation) }}
                      </a>
                    </div>
                  </div>

                  <!-- Email -->
                  <div
                    v-if="storeLocation?.email"
                    class="flex items-start gap-3"
                  >
                    <div
                      class="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <v-icon color="white" size="16">mdi-email</v-icon>
                    </div>
                    <div>
                      <h4 class="text-base font-semibold mb-1" :style="{ color: 'var(--contact-label-color)' }">
                        Email
                      </h4>
                      <a
                        :href="`mailto:${storeLocation.email}`"
                        class="text-base font-medium hover:underline break-all"
                        :style="{ color: 'var(--contact-link-color)' }"
                      >
                        {{ storeLocation.email }}
                      </a>
                    </div>
                  </div>

                  <!-- Address -->
                  <div class="flex items-start gap-3">
                    <div class="flex-1">
                      <h4 class="text-base font-semibold mb-1" :style="{ color: 'var(--contact-label-color)' }">
                        Address
                      </h4>
                      <div class="text-base" :style="{ color: 'var(--contact-text-color)' }">
                        <div class="font-medium mb-1">
                          {{
                            storeLocation?.name ||
                            storeLocation?.storeLocation ||
                            "Our Location"
                          }}
                        </div>
                        <div>{{ getFullAddress(storeLocation) }}</div>
                      </div>
                    </div>
                    <!-- Get Directions Button -->
                    <div v-if="directionsUrl" class="flex-shrink-0">
                      <a
                        :href="directionsUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm"
                        :style="{
                          backgroundColor: 'var(--store-button-primary)',
                          color: 'var(--store-button-text)',
                        }"
                      >
                        <v-icon size="16">mdi-directions</v-icon>
                        Get Directions
                      </a>
                    </div>
                    <!-- Debug info for directions button -->
                    <div v-else class="flex-shrink-0 text-xs text-gray-500">
                      <div>Debug: Directions URL: {{ directionsUrl || 'No URL' }}</div>
                      <div>Debug: storeLocation exists: {{ !!storeLocation }}</div>
                      <div>Debug: apifyLat: {{ apifyLat }}, apifyLng: {{ apifyLng }}</div>
                      <div>Debug: Full Address: {{ getFullAddress(storeLocation) }}</div>
                      <div>Debug: Brand Name: {{ brandName }}</div>
                      <button 
                        @click="testAddressConstruction" 
                        class="mt-2 px-2 py-1 bg-gray-300 text-xs rounded"
                      >
                        Test Address
                      </button>
                    </div>
                  </div>

                  <!-- Website -->
                  <div
                    v-if="storeLocation?.website"
                    class="flex items-start gap-3"
                  >
                    <div
                      class="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <v-icon color="white" size="16">mdi-web</v-icon>
                    </div>
                    <div>
                      <h4 class="text-base font-semibold mb-1" :style="{ color: 'var(--contact-label-color)' }">
                        Website
                      </h4>
                      <a
                        :href="storeLocation.website"
                        target="_blank"
                        class="text-base font-medium hover:underline"
                        :style="{ color: 'var(--contact-link-color)' }"
                      >
                        {{ formatWebsite(storeLocation.website) }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Business Hours -->
              <div 
                class="rounded-xl p-4"
                :style="{
                  backgroundColor: 'var(--contact-card-bg-color)',
                  borderRadius: 'var(--contact-card-border-radius)',
                  border: '1px solid var(--contact-card-border-color)'
                }"
              >
                <h3 
                  class="text-lg font-bold mb-3"
                  :style="{ color: 'var(--primary-title-color)' }"
                >
                  Business Hours
                </h3>
                <div class="space-y-1">
                  <div
                    v-for="hours in getOpeningHours(storeLocation)"
                    :key="hours.day"
                    class="flex justify-between items-center py-1 border-b border-gray-200 last:border-b-0"
                  >
                    <span class="text-sm font-medium" :style="{ color: 'var(--contact-label-color)' }">{{
                      hours.day
                    }}</span>
                    <span class="text-sm" :style="{ color: 'var(--contact-text-color)' }">{{ hours.hours }}</span>
                  </div>
                  <div
                    v-if="!getOpeningHours(storeLocation).length"
                    class="text-center py-2 text-sm"
                    :style="{ color: 'var(--contact-description-color)' }"
                  >
                    Business hours not available
                  </div>
                </div>
              </div>

              <!-- Social Media -->
              <div
                v-if="getSocialLinks(storeLocation).length > 0"
                class="rounded-xl p-4"
                :style="{
                  backgroundColor: 'var(--contact-card-bg-color)',
                  borderRadius: 'var(--contact-card-border-radius)',
                  border: '1px solid var(--contact-card-border-color)'
                }"
              >
                <h3 
                  class="text-lg font-bold mb-3"
                  :style="{ color: 'var(--primary-title-color)' }"
                >
                  Find us on social media
                </h3>
                <div class="flex flex-wrap gap-2">
                  <a
                    v-for="social in getSocialLinks(storeLocation)"
                    :key="social.platform"
                    :href="social.url"
                    target="_blank"
                    class="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 hover:shadow-md"
                  >
                    <v-icon :color="social.iconColor" size="16">{{
                      social.icon
                    }}</v-icon>
                    <span class="text-xs font-medium text-gray-700">{{
                      social.platform
                    }}</span>
                  </a>
                </div>
              </div>
            </div>

            <!-- Right Column: Map -->
            <div 
              class="rounded-xl p-4 h-full"
              :style="{
                backgroundColor: 'var(--contact-card-bg-color)',
                borderRadius: 'var(--contact-card-border-radius)',
                border: '1px solid var(--contact-card-border-color)'
              }"
            >
              <h3 
                class="text-lg font-bold mb-3"
                :style="{ color: 'var(--primary-title-color)' }"
              >
                Find us on Google Maps
              </h3>
              <div
                class="flex-1 rounded-lg overflow-hidden shadow-lg border border-gray-200"
                style="height: calc(100% - 60px)"
              >
                <iframe
                  v-if="
                    storeLocation &&
                    ((apifyLat && apifyLng) ||
                      storeLocation.place_id ||
                      getFullAddress(storeLocation))
                  "
                  :src="getGoogleMapsSrc()"
                  width="100%"
                  height="100%"
                  style="border: 0"
                  allowfullscreen
                  loading="lazy"
                  referrerpolicy="no-referrer-when-downgrade"
                >
                </iframe>
                <div
                  v-else
                  class="flex items-center justify-center h-full bg-gray-100 text-gray-500"
                >
                  <div class="text-center">
                    <v-icon size="24" color="gray" class="mb-2"
                      >mdi-map-outline</v-icon
                    >
                    <p class="text-sm font-semibold">Map not available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Gallery Section -->
      <section
        id="posts"
        v-if="
          showPostsSection &&
          galleryPosts.length > 0 &&
          (currentSection === 'posts' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        class="py-16 px-6"
        :style="{ backgroundColor: 'var(--posts-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <h2 
              class="text-3xl md:text-4xl font-bold mb-4"
              :style="{ color: 'var(--primary-title-color)' }"
            >
              {{ getPostsSectionTitle() }}
            </h2>
            <p class="text-lg text-gray-600">
              {{ getPostsSectionSubtext() }}
            </p>
          </div>

          <!-- Loading State -->
          <div v-if="isGalleryLoading" class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
            ></v-progress-circular>
            <p class="text-gray-500 mt-2">Loading posts...</p>
          </div>

          <!-- Posts Grid -->
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start post-grid">
            <div
              v-for="post in galleryPosts"
              :key="post.id"
              class="rounded-lg p-6 shadow-sm border flex flex-col items-start post-card cursor-pointer hover:shadow-md transition-shadow duration-200"
              :style="{
                backgroundColor: 'var(--post-card-bg-color)',
                borderRadius: 'var(--post-card-border-radius)',
                borderColor: 'var(--post-card-border-color)'
              }"
              @click="openPostModal(post)"
            >
              <!-- Date Only -->
              <div class="flex items-center gap-2 mb-4">
                <span class="text-xs text-gray-500">
                  {{
                    formatDate(
                      post.created_at?.seconds
                        ? post.created_at.seconds * 1000
                        : post.created_at
                    )
                  }}
                </span>
              </div>

              <!-- Images on Top - Responsive Size -->
              <div v-if="post.images && post.images.length > 0" class="mb-4 w-full">
                <div class="grid grid-cols-1 gap-2 w-full">
                  <div class="flex justify-center w-full">
                    <img
                      v-for="(image, index) in post.images.slice(0, 1)"
                      :key="index"
                      :src="image"
                      alt="post image"
                      class="w-full h-32 md:h-40 object-cover bg-gray-100 rounded-lg shadow-md"
                    />
                  </div>
                  <div
                    v-if="post.images.length > 1"
                    class="grid grid-cols-2 gap-1 w-full"
                  >
                    <div class="flex justify-center w-full">
                      <img
                        v-for="(image, index) in post.images.slice(1, 2)"
                        :key="index"
                        :src="image"
                        alt="post image"
                        class="w-full h-16 md:h-20 object-cover bg-gray-100 rounded-lg shadow-sm"
                      />
                    </div>
                    <div class="flex justify-center w-full">
                      <img
                        v-for="(image, index) in post.images.slice(2, 3)"
                        :key="index"
                        :src="image"
                        alt="post image"
                        class="w-full h-16 md:h-20 object-cover bg-gray-100 rounded-lg shadow-sm"
                      />
                    </div>
                    <div
                      v-if="post.images.length > 3"
                      class="h-16 md:h-20 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500"
                    >
                      +{{ post.images.length - 3 }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Videos on Top - Responsive Size -->
              <div v-if="post.videos && post.videos.length > 0" class="mb-4">
                <div class="grid grid-cols-1 gap-2">
                  <div
                    v-for="(video, index) in post.videos.slice(0, 1)"
                    :key="index"
                    class="w-full h-32 md:h-40 bg-gray-200 rounded-lg flex items-center justify-center shadow-md relative"
                  >
                    <v-icon size="32" color="white" class="absolute"
                      >mdi-play-circle</v-icon
                    >
                    <div
                      class="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded"
                    >
                      Video
                    </div>
                  </div>
                  <div
                    v-if="post.videos.length > 1"
                    class="grid grid-cols-2 gap-1"
                  >
                    <div
                      v-for="(video, index) in post.videos.slice(1, 3)"
                      :key="index"
                      class="h-16 md:h-20 bg-gray-200 rounded-lg flex items-center justify-center relative"
                    >
                      <v-icon size="16" color="white">mdi-play</v-icon>
                    </div>
                    <div
                      v-if="post.videos.length > 3"
                      class="h-16 md:h-20 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500"
                    >
                      +{{ post.videos.length - 3 }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Post Title -->
              <div
                v-if="post.name"
                class="text-base font-semibold mb-2"
                :style="{ color: 'var(--post-title-color)' }"
              >
                {{ post.name }}
              </div>

              <!-- Post Text -->
              <div class="text-sm leading-relaxed flex-grow w-full" :style="{ color: 'var(--post-text-color)' }">
                <span v-if="post.text">
                  {{
                    post.text.length > 150 && !expandedPosts[post.id]
                      ? post.text.substring(0, 150) + "..."
                      : post.text
                  }}
                </span>
                <span
                  v-if="post.text && post.text.length > 150"
                  @click.stop="toggleReadMore(post.id)"
                  class="text-blue-500 hover:underline ml-1 font-semibold text-xs cursor-pointer"
                >
                  {{ expandedPosts[post.id] ? "Show less" : "Show more" }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Blog Section -->
      <section
        id="blogs"
        v-if="
          showBlogsSection &&
          blogs.length > 0 &&
          (currentSection === 'blogs' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        class="py-16 px-6"
        :style="{ backgroundColor: 'var(--blog-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <h2 
              class="text-3xl md:text-4xl font-bold mb-4"
              :style="{ color: 'var(--primary-title-color)' }"
            >
              {{ getBlogSectionTitle() }}
            </h2>
            <p class="text-lg text-gray-600">
              {{ getBlogSectionSubtext() }}
            </p>
          </div>

          <!-- Loading State -->
          <div v-if="isBlogsLoading" class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
            ></v-progress-circular>
            <p class="text-gray-500 mt-2">Loading blog posts...</p>
          </div>

          <!-- Blog Posts Grid -->
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div
              v-for="blog in blogs"
              :key="blog.id"
              class="rounded-lg overflow-hidden shadow-lg cursor-pointer"
              :style="{
                backgroundColor: 'var(--blog-card-bg-color)',
                borderRadius: 'var(--blog-card-border-radius)',
                borderColor: 'var(--blog-card-border-color)'
              }"
              @click="openBlogDetail(blog)"
            >
              <!-- Blog Image -->
              <div class="h-48 overflow-hidden">
                <img
                  v-if="blog.image"
                  :src="blog.image"
                  :alt="blog.title"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center"
                >
                  <v-icon size="48" color="blue lighten-2">mdi-post-outline</v-icon>
                </div>
              </div>

              <!-- Blog Content -->
              <div class="p-6">
                <!-- Blog Meta -->
                <div class="flex items-center justify-between mb-3">
                  <span class="text-xs" :style="{ color: 'var(--blog-meta-color)' }">
                    {{ formatBlogDate(blog.publishedAt) }}
                  </span>
                </div>

                <!-- Blog Title -->
                <h3 class="text-xl font-bold mb-3 line-clamp-2" :style="{ color: 'var(--blog-title-color)' }">
                  {{ blog.title }}
                </h3>

                <!-- Blog Excerpt -->
                <p class="text-sm leading-relaxed line-clamp-3 mb-4" :style="{ color: 'var(--blog-excerpt-color)' }">
                  {{ getBlogExcerpt(blog.text) }}
                </p>

                <!-- Blog Tags -->
                <div v-if="blog.tags && blog.tags.length > 0" class="flex flex-wrap gap-2 mb-4">
                  <v-chip
                    v-for="tag in blog.tags.slice(0, 3)"
                    :key="tag"
                    x-small
                    outlined
                    :color="getBlogTagColor()"
                  >
                    {{ tag }}
                  </v-chip>
                  <span
                    v-if="blog.tags.length > 3"
                    class="text-xs"
                    :style="{ color: 'var(--blog-meta-color)' }"
                  >
                    +{{ blog.tags.length - 3 }} more
                  </span>
                </div>

                <!-- Read More Button -->
                <div class="flex items-center justify-between">
                  <span class="text-blue-600 font-semibold text-sm">
                    Read More
                  </span>
                  <v-icon size="16" color="blue">mdi-arrow-right</v-icon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Blog Detail Section -->
      <section
        id="blog-detail"
        v-if="
          showBlogDetailSection &&
          currentBlog &&
          currentSection === 'blog-detail'
        "
        class="py-16 px-6 bg-white"
      >
        <div class="max-w-4xl mx-auto">
          <!-- Back to Blogs Button -->
          <div class="mb-8">
            <button
              @click="backToBlogs"
              class="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold mb-6"
            >
              <v-icon size="20">mdi-arrow-left</v-icon>
              Back to Blog Posts
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="isBlogDetailLoading" class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
            ></v-progress-circular>
            <p class="text-gray-500 mt-2">Loading blog post...</p>
          </div>

          <!-- Blog Detail Content -->
          <div v-else-if="currentBlog" class="blog-detail-content">
            <!-- Blog Header -->
            <div class="mb-8">
              <!-- Blog Meta -->
              <div class="flex items-center gap-4 mb-4 text-sm text-gray-500">
                <span>Published on {{ formatBlogDate(currentBlog.publishedAt) }}</span>
              </div>

              <!-- Blog Title -->
              <h1 class="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                {{ currentBlog.title }}
              </h1>

              <!-- Blog Image -->
              <div v-if="currentBlog.image" class="mb-8">
                <img
                  :src="currentBlog.image"
                  :alt="currentBlog.title"
                  class="w-full h-auto max-h-96 object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>

            <!-- Blog Content -->
            <div class="prose prose-lg max-w-none">
              <div class="text-gray-700 leading-relaxed text-lg" v-html="sanitizeHtml(currentBlog.text)">
              </div>
            </div>

            <!-- Blog Footer -->
            <div class="mt-12 pt-8 border-t border-gray-200">
              <div v-if="currentBlog.tags && currentBlog.tags.length > 0" class="flex items-center gap-4">
                <span class="text-sm text-gray-500">
                  Tags: {{ currentBlog.tags.join(", ") }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Product Detail Section -->
      <section
        id="product-detail"
        v-if="
          showProductDetailSection &&
          currentProduct &&
          currentSection === 'product-detail'
        "
        class="py-16 px-6 bg-white"
      >
        <div class="max-w-4xl mx-auto">
          <!-- Back to Products Button -->
          <div class="mb-8">
            <button
              @click="backToProducts"
              class="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold mb-6"
            >
              <v-icon size="20">mdi-arrow-left</v-icon>
              Back to Products & Services
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="isProductDetailLoading" class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
            ></v-progress-circular>
            <p class="text-gray-500 mt-2">Loading product details...</p>
          </div>

          <!-- Product Detail Content -->
          <div v-else-if="currentProduct" class="product-detail-content">
            <!-- Two Column Layout: Media on Left, Info on Right -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              
              <!-- Left Column: Media Gallery -->
              <div class="media-gallery">
                <div v-if="getAllMedia(currentProduct).length > 0" class="space-y-6">
                  <!-- Main Featured Media -->
                  <div class="mb-6">
                                      <div 
                    v-if="getAllMedia(currentProduct)[0].type === 'image'"
                    class="relative group cursor-pointer"
                    @click="openProductMediaModal(currentProduct, 0)"
                  >
                      <img
                        :src="getAllMedia(currentProduct)[0].url"
                        :alt="`${currentProduct.name} - Featured image`"
                        class="w-full h-96 object-cover rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
                      />
                      <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                        <v-icon 
                          v-if="!imageModalVisible" 
                          color="white" 
                          size="32"
                          class="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        >
                          mdi-magnify
                        </v-icon>
                      </div>
                    </div>
                    
                    <div 
                      v-else-if="getAllMedia(currentProduct)[0].type === 'video'"
                      class="w-full"
                    >
                      <!-- YouTube Video Embed -->
                      <div v-if="isYouTubeUrl(getAllMedia(currentProduct)[0].url)" class="aspect-video">
                        <iframe
                          :src="getYouTubeEmbedUrl(getAllMedia(currentProduct)[0].url)"
                          :title="`${currentProduct.name} - Featured video`"
                          class="w-full h-full rounded-lg shadow-lg"
                          frameborder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowfullscreen
                        ></iframe>
                      </div>
                      
                      <!-- Direct Video File -->
                      <video 
                        v-else
                        :src="getAllMedia(currentProduct)[0].url"
                        :title="`${currentProduct.name} - Featured video`"
                        controls
                        class="w-full h-96 object-cover rounded-lg shadow-lg"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>

                  <!-- Thumbnail Gallery -->
                  <div v-if="getAllMedia(currentProduct).length > 1" class="grid grid-cols-4 gap-3">
                                      <div 
                    v-for="(media, index) in getAllMedia(currentProduct).slice(1)" 
                    :key="index"
                    class="relative group cursor-pointer"
                    @click="openProductMediaFromThumbnail(currentProduct, index + 1)"
                  >
                      <!-- Image Thumbnail -->
                      <div v-if="media.type === 'image'" class="aspect-square">
                        <img
                          :src="media.url"
                          :alt="`${currentProduct.name} - Media ${index + 2}`"
                          class="w-full h-full object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                        />
                        <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                          <v-icon 
                            color="white" 
                            size="20"
                            class="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          >
                            mdi-magnify
                          </v-icon>
                        </div>
                      </div>
                      
                      <!-- Video Thumbnail -->
                      <div v-else-if="media.type === 'video'" class="aspect-square relative">
                        <div class="w-full h-full bg-gray-200 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex items-center justify-center">
                          <v-icon 
                            color="gray" 
                            size="32"
                            class="group-hover:text-blue-600 transition-colors duration-300"
                          >
                            mdi-play-circle
                          </v-icon>
                        </div>
                        <div class="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                          Video
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Fallback for single image (old data structure) -->
                <div v-else-if="currentProduct.image" class="mb-8">
                  <img
                    :src="currentProduct.image"
                    :alt="currentProduct.name"
                    class="w-full h-auto max-h-96 object-cover rounded-lg shadow-lg"
                  />
                </div>
              </div>

              <!-- Right Column: Product Information -->
              <div class="product-info">
                <!-- Product Title -->
                <h1 class="text-3xl md:text-4xl font-bold text-gray-800 mb-6 leading-tight">
                  {{ currentProduct.name }}
                </h1>

                <!-- Product Price -->
                <div v-if="getServicePrice(currentProduct)" class="mb-6">
                  <span class="text-2xl font-semibold text-blue-600">
                    {{ getServicePrice(currentProduct) }}
                  </span>
                </div>

                <!-- Product Description -->
                <div class="prose prose-lg max-w-none mb-8">
                  <div v-if="currentProduct.description" class="text-gray-700 leading-relaxed text-lg">
                    {{ currentProduct.description }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Gallery Section -->
      <section
        id="gallery"
        v-if="
          showGallerySection &&
          displayedGalleryImages.length > 0 &&
          (currentSection === 'gallery' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        class="py-16 px-6"
        :style="{ backgroundColor: 'var(--gallery-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <h2 
              class="text-3xl md:text-4xl font-bold mb-4"
              :style="{ color: 'var(--gallery-title-color)' }"
            >
              {{ getGallerySectionTitle() }}
            </h2>
            <p class="text-lg" :style="{ color: 'var(--gallery-subtitle-color)' }">
              {{ getGallerySectionSubtext() }}
            </p>
          </div>

          <!-- Loading State -->
          <div v-if="isGalleryLoading" class="text-center py-8">
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
            ></v-progress-circular>
            <p class="text-gray-500 mt-2">Loading gallery...</p>
          </div>

          <!-- Gallery Grid -->
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div
              v-for="(image, index) in displayedGalleryImages"
              :key="index"
              v-show="!image.loadError"
              class="group cursor-pointer gallery-item"
              @click="openGalleryModal(index)"
            >
              <div 
                class="relative overflow-hidden transition-all duration-300 transform hover:scale-105 gallery-item-container"
                :style="{
                  borderRadius: 'var(--gallery-card-border-radius)',
                  boxShadow: 'var(--gallery-image-shadow)',
                  backgroundColor: 'var(--gallery-card-bg-color)',
                  border: '1px solid var(--gallery-card-border-color)',
                }"
              >
                <img
                  :src="getGalleryImageUrl(image)"
                  :alt="`Gallery image ${index + 1}`"
                  class="w-full h-48 object-cover"
                  :style="{ borderRadius: 'var(--gallery-image-border-radius)' }"
                  loading="lazy"
                  @error="handleGalleryImageError($event, image, index)"
                  @load="handleGalleryImageLoad(image, index)"
                />
                <div 
                  class="absolute inset-0 transition-all duration-300 flex items-center justify-center gallery-overlay"
                  :style="{ backgroundColor: 'var(--gallery-overlay-color)' }"
                  style="opacity: 0;"
                >
                  <v-icon 
                    size="32" 
                    :style="{ color: 'var(--gallery-icon-color)' }"
                    class="transition-opacity duration-300"
                    style="opacity: 0;"
                  >
                    mdi-magnify
                  </v-icon>
                </div>
              </div>
            </div>
          </div>

          <!-- Show More Button -->
          <div v-if="galleryImages.length > maxGalleryImages && currentSection === 'home'" class="text-center mt-8">
            <button
              @click="navigateToSection('gallery')"
              class="px-6 py-3 font-semibold rounded-lg transition-colors duration-200"
              :style="{
                backgroundColor: 'var(--gallery-button-bg-color)',
                color: 'var(--gallery-button-text-color)',
              }"
              @mouseenter="$event.target.style.backgroundColor = 'var(--gallery-button-hover-bg-color)'; $event.target.style.color = 'var(--gallery-button-hover-text-color)'"
              @mouseleave="$event.target.style.backgroundColor = 'var(--gallery-button-bg-color)'; $event.target.style.color = 'var(--gallery-button-text-color)'"
            >
              View All {{ galleryImages.length }} Photos
            </button>
          </div>
        </div>
      </section>

      <!-- Social Media Links Section -->
      <section
        id="social-media"
        v-if="
          showSocialMediaSection &&
          socialMediaLinks.length > 0 &&
          (currentSection === 'social-media' ||
            currentSection === 'home' ||
            currentSection === '')
        "
        class="py-16 px-6"
        :style="{ backgroundColor: 'var(--social-section-bg-color)' }"
      >
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <h2 class="text-3xl md:text-4xl font-bold mb-4" :style="{ color: 'var(--social-title-color)' }">
              {{ getSocialSectionTitle() }}
            </h2>
            <p class="text-lg" :style="{ color: 'var(--social-subtitle-color)' }">
              {{ getSocialSectionSubtext() }}
            </p>
          </div>
          <div class="flex justify-center items-center gap-6">
            <a
              v-for="social in socialMediaLinks"
              :key="social.platform"
              :href="social.url"
              target="_blank"
              rel="noopener noreferrer"
              class="transition-colors duration-200 hover:scale-110"
              :style="{ color: 'var(--social-icon-color)' }"
              @mouseenter="$event.target.style.color = 'var(--social-icon-hover-color)'"
              @mouseleave="$event.target.style.color = 'var(--social-icon-color)'"
            >
              <v-icon size="48">{{
                social.icon
              }}</v-icon>
            </a>
          </div>
        </div>
      </section>

      <!-- Footer -->
      <footer
        v-if="showFooter"
        class="border-t py-8 px-6"
        :style="{
          backgroundColor: 'var(--footer-bg-color)',
          borderColor: 'var(--footer-border-color)',
          boxShadow: `0 -4px 6px -1px var(--footer-shadow-color)`,
          color: 'var(--footer-text-color)'
        }"
      >
        <div class="max-w-4xl mx-auto text-center">
          <div class="flex items-center justify-center gap-3 mb-4">
            <img
              v-if="storeLogo"
              :src="storeLogo"
              :alt="brandName"
              class="h-8 w-auto object-contain"
            />
            <h3 class="text-lg font-semibold" :style="{ color: 'var(--footer-brand-name-color)' }">{{ brandName }}</h3>
          </div>

          <div class="text-sm mb-4" :style="{ color: 'var(--footer-text-color)' }">
            <p v-if="getGmbPhone(storeLocation)" class="mb-1">
              <a
                :href="`tel:${getGmbPhone(storeLocation)}`"
                class="hover:underline"
                :style="{ color: 'var(--footer-text-color)' }"
              >
                {{ getGmbPhone(storeLocation) }}
              </a>
            </p>
            <p v-if="storeLocation?.email" class="mb-1">
              <a
                :href="`mailto:${storeLocation.email}`"
                class="hover:underline"
                :style="{ color: 'var(--footer-text-color)' }"
              >
                {{ storeLocation.email }}
              </a>
            </p>
            <div class="flex items-center justify-center gap-2">
              <span>{{ getFullAddress(storeLocation) }}</span>
              <a
                v-if="directionsUrl"
                :href="directionsUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 transition-colors"
                :style="{ color: 'var(--footer-text-color)' }"
                title="Get Directions"
              >
                <v-icon size="14">mdi-directions</v-icon>
              </a>
            </div>
          </div>

          <div class="border-t pt-4 text-xs" :style="{ borderColor: 'var(--footer-border-color)', color: 'var(--footer-copyright-color)' }">
            <p>
              &copy; {{ new Date().getFullYear() }} {{ brandName }}. All rights
              reserved.
            </p>
            <p class="mt-2">
              <a href="https://fly-social.com" target="_blank" class="hover:underline" :style="{ color: 'var(--footer-copyright-color)' }">Powered by Fly</a>
            </p>
          </div>
        </div>
      </footer>

      <!-- Post Modal -->
      <div v-if="showPostModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closePostModal">
        <div class="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" @click.stop>
          <!-- Modal Header with Posted Time -->
          <div class="flex items-center justify-between p-6 border-b border-gray-200">
            <div class="flex items-center gap-2">
              <span class="text-sm text-gray-500">
                {{ formatDate(selectedPost?.created_at?.seconds ? selectedPost.created_at.seconds * 1000 : selectedPost?.created_at) }}
              </span>
            </div>
            <button @click="closePostModal" class="text-gray-500 hover:text-gray-700">
              <v-icon size="24">mdi-close</v-icon>
            </button>
          </div>

          <!-- Modal Content -->
          <div class="p-6">

            <!-- Images -->
            <div v-if="selectedPost?.images && selectedPost.images.length > 0" class="mb-6">
              <div class="grid grid-cols-1 gap-4">
                <div v-for="(image, index) in selectedPost.images" :key="index" class="bg-gray-100 rounded-lg p-4">
                  <img
                    :src="image"
                    :alt="`Post image ${index + 1}`"
                    class="w-full object-contain rounded-lg"
                  />
                </div>
              </div>
            </div>

            <!-- Videos -->
            <div v-if="selectedPost?.videos && selectedPost.videos.length > 0" class="mb-6">
              <div class="grid grid-cols-1 gap-4">
                <div
                  v-for="(video, index) in selectedPost.videos"
                  :key="index"
                  class="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center shadow-md relative"
                >
                  <v-icon size="48" color="white" class="absolute">mdi-play-circle</v-icon>
                  <div class="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white text-sm px-3 py-1 rounded">
                    Video
                  </div>
                </div>
              </div>
            </div>

            <!-- Post Title -->
            <div v-if="selectedPost?.name" class="text-xl font-bold text-gray-800 mb-4">
              {{ selectedPost.name }}
            </div>

            <!-- Post Text -->
            <div v-if="selectedPost?.text" class="text-gray-700 leading-relaxed text-base">
              {{ selectedPost.text }}
            </div>
          </div>
        </div>
      </div>

      <!-- Gallery Modal -->
      <div v-if="showGalleryModal" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" @click="closeGalleryModal">
        <div class="relative max-w-4xl w-full max-h-[90vh]" @click.stop>
          <!-- Close Button -->
          <button 
            @click="closeGalleryModal" 
            class="absolute top-4 right-4 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-2 shadow-lg border border-gray-200"
          >
            <v-icon size="24">mdi-close</v-icon>
          </button>

          <!-- Navigation Buttons -->
          <button 
            v-if="galleryImages.length > 1"
            @click="previousGalleryImage" 
            class="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-3 shadow-lg border border-gray-200"
          >
            <v-icon size="28">mdi-chevron-left</v-icon>
          </button>
          <button 
            v-if="galleryImages.length > 1"
            @click="nextGalleryImage" 
            class="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-3 shadow-lg border border-gray-200"
          >
            <v-icon size="28">mdi-chevron-right</v-icon>
          </button>

          <!-- Current Image -->
          <div class="flex items-center justify-center h-full">
            <img
              v-if="currentGalleryImage && !currentGalleryImage.loadError"
              :src="currentGalleryImage.googleUrl"
              :alt="`Gallery image ${currentGalleryIndex + 1}`"
              class="max-w-full max-h-full object-contain rounded-lg"
              @error="handleModalImageError"
            />
            <div v-else-if="currentGalleryImage && currentGalleryImage.loadError" class="text-center text-white">
              <v-icon size="64" color="white" class="mb-4">mdi-image-off</v-icon>
              <p>Image not available</p>
            </div>
          </div>

          <!-- Image Counter -->
          <div v-if="galleryImages.length > 1" class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 text-gray-800 text-sm px-4 py-2 rounded-full shadow-lg border border-gray-200">
            {{ currentGalleryIndex + 1 }} / {{ galleryImages.length }}
          </div>

          <!-- Keyboard Navigation Hint -->
          <div class="absolute bottom-4 right-4 bg-white bg-opacity-90 text-gray-800 text-xs px-3 py-2 rounded-full shadow-lg border border-gray-200">
            Use ← → keys to navigate
          </div>
        </div>
      </div>

      <!-- Image Modal -->
      <div v-if="imageModalVisible" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" @click="closeImageModal">
        <div class="relative max-w-4xl w-full max-h-[90vh]" @click.stop>
          <!-- Close Button -->
          <button 
            @click="closeImageModal" 
            class="absolute top-4 right-4 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-2 shadow-lg border border-gray-200"
          >
            <v-icon size="24">mdi-close</v-icon>
          </button>

          <!-- Navigation Buttons -->
          <button 
            v-if="currentProductMedia.length > 1"
            @click="previousProductMedia" 
            class="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-3 shadow-lg border border-gray-200"
          >
            <v-icon size="28">mdi-chevron-left</v-icon>
          </button>
          <button 
            v-if="currentProductMedia.length > 1"
            @click="nextProductMedia" 
            class="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-3 shadow-lg border border-gray-200"
          >
            <v-icon size="28">mdi-chevron-right</v-icon>
          </button>

          <!-- Current Image -->
          <div class="flex items-center justify-center h-full">
            <img
              v-if="currentImageUrl"
              :src="currentImageUrl"
              :alt="'Product image'"
              class="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>

          <!-- Media Counter -->
          <div v-if="currentProductMedia.length > 1" class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 text-gray-800 text-sm px-4 py-2 rounded-full shadow-lg border border-gray-200">
            {{ currentMediaIndex + 1 }} / {{ currentProductMedia.length }}
          </div>

          <!-- Keyboard Navigation Hint -->
          <div class="absolute bottom-4 right-4 bg-white bg-opacity-90 text-gray-800 text-xs px-3 py-2 rounded-full shadow-lg border border-gray-200">
            Use ← → keys to navigate
          </div>
        </div>
      </div>

      <!-- Video Modal -->
      <div v-if="videoModalVisible" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" @click="closeVideoModal">
        <div class="relative max-w-4xl w-full max-h-[90vh]" @click.stop>
          <!-- Close Button -->
          <button 
            @click="closeVideoModal" 
            class="absolute top-4 right-4 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-2 shadow-lg border border-gray-200"
          >
            <v-icon size="24">mdi-close</v-icon>
          </button>

          <!-- Navigation Buttons -->
          <button 
            v-if="currentProductMedia.length > 1"
            @click="previousProductMedia" 
            class="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-3 shadow-lg border border-gray-200"
          >
            <v-icon size="28">mdi-chevron-left</v-icon>
          </button>
          <button 
            v-if="currentProductMedia.length > 1"
            @click="nextProductMedia" 
            class="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-800 hover:text-black transition-all duration-200 rounded-full p-3 shadow-lg border border-gray-200"
          >
            <v-icon size="28">mdi-chevron-right</v-icon>
          </button>

          <!-- Current Video -->
          <div class="flex items-center justify-center h-full">
            <!-- YouTube Video Embed -->
            <div v-if="isYouTubeUrl(currentVideoUrl)" class="w-full aspect-video">
              <iframe
                :src="getYouTubeEmbedUrl(currentVideoUrl)"
                :title="'Product video'"
                class="w-full h-full rounded-lg"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              ></iframe>
            </div>
            
            <!-- Direct Video File -->
            <video 
              v-else
              :src="currentVideoUrl"
              :title="'Product video'"
              controls
              class="w-full max-h-full rounded-lg"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          <!-- Media Counter -->
          <div v-if="currentProductMedia.length > 1" class="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-90 text-gray-800 text-sm px-4 py-2 rounded-full shadow-lg border border-gray-200">
            {{ currentMediaIndex + 1 }} / {{ currentProductMedia.length }}
          </div>

          <!-- Keyboard Navigation Hint -->
          <div class="absolute bottom-4 right-4 bg-white bg-opacity-90 text-gray-800 text-xs px-3 py-2 rounded-full shadow-lg border border-gray-200">
            Use ← → keys to navigate
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from "@/utils/firebase.utils";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  orderBy,
  limit,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { auth } from "@/utils/firebase.utils";
import { isValidSubdomain, extractSubdomainName, getCurrentEnvironment, getDomainConfig } from "@/utils/domainConfig";

export default {
  props: {
    // NOTE: These props are used as fallbacks for the effective computed properties
    // The effective values (effectiveUserUid, effectiveWid, effectiveLocationId, effectiveSlug)
    // should be used throughout the component instead of these direct props
    userUid: {
      type: String,
      default: null,
    },
    wid: {
      type: String,
      default: null,
    },
    locationId: {
      type: String,
      default: null,
    },
    slug: {
      type: String,
      default: null,
    },
    disableTitleAndFavicon: {
      type: Boolean,
      default: false,
    },
    draftLocationStyle: {
      type: Object,
      default: null,
    },
    draftHeaderStyle: {
      type: Object,
      default: null,
    },
    draftCtaAppointments: {
      type: Object,
      default: null,
    },
    draftHeroSection: {
      type: Object,
      default: null,
    },
    draftReviewsSection: {
      type: Object,
      default: null,
    },
    draftContactInformation: {
      type: Object,
      default: null,
    },
    draftAppointmentStyles: {
      type: Object,
      default: null,
    },
    draftProductStyles: {
      type: Object,
      default: null,
    },
    draftPostsSection: {
      type: Object,
      default: null,
    },
    draftBlogSection: {
      type: Object,
      default: null,
    },
    draftFooterSection: {
      type: Object,
      default: null,
    },
    draftSocialSection: {
      type: Object,
      default: null,
    },
    draftSocialLinksStyles: {
      type: Object,
      default: null,
    },
    draftGallerySection: {
      type: Object,
      default: null,
    },
    draftSectionHeaders: {
      type: Object,
      default: null,
    },
    embedSlug: {
      type: String,
      default: null,
    },

  },
  data() {
    return {
      isLoading: true,
      storeLocation: null,
      workspaceData: null,
      gmbSettingsData: null, // Add this to store GMB settings data
      apifyLat: null,
      apifyLng: null,
      logoLoadError: false,
      coverPhotoError: false,
      coverPhotoLoading: false,
      reviews: [],
      isReviewsLoading: false,
      reviewsPage: 1,
      reviewsPerPage: 5,
      totalReviews: 0,
      _originalFaviconHref: null,
      _originalPageTitle: null,
      appointmentForm: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        date: "",
        time: "",
        service: "",
        message: "",
      },
      validationErrors: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        date: "",
        time: "",
      },
      isSubmitting: false,
      isEnablingAppointments: false,
      galleryPosts: [],
      isGalleryLoading: false,
      expandedPosts: {},
      // Blog data
      blogs: [],
      isBlogsLoading: false,
      // Subdomain data
      subdomainData: null,
      isSubdomainLoading: false,
      // Current active section
      currentSection: "home",
      currentBlog: null,
      isBlogDetailLoading: false,
      // Product detail data
      currentProduct: null,
      isProductDetailLoading: false,
      // Image modal data
      imageModalVisible: false,
      currentImageUrl: '',
      currentMediaIndex: 0,
      currentProductMedia: [],
      // Video modal data
      videoModalVisible: false,
      currentVideoUrl: '',
      // Post modal data
      showPostModal: false,
      selectedPost: null,
      // Gallery data
      galleryImages: [],
      isGalleryLoading: false,
      showGalleryModal: false,
      currentGalleryIndex: 0,
      maxGalleryImages: 12, // Show only 12 images in home section
    };
  },
  computed: {
    // Computed property for the directions URL
    directionsUrl() {
      if (this.apifyLat && this.apifyLng) {
        return `https://www.google.com/maps/dir//${this.apifyLat},${this.apifyLng}`;
      }
      
      if (this.storeLocation) {
        const fullAddress = this.getFullAddress(this.storeLocation);
        const addrParts = [
          this.storeLocation.name || this.storeLocation.storeLocation,
          fullAddress,
        ].filter(Boolean);
        
        const addr = addrParts.join(", ");
        if (addr.trim()) {
          return `https://www.google.com/maps/dir//${encodeURIComponent(addr)}`;
        }
      }
      
      if (this.brandName && this.brandName !== "Store Brand") {
        return `https://www.google.com/maps/dir//${encodeURIComponent(this.brandName)}`;
      }
      
      return "";
    },
    isSubdomain() {
      const hostname = window.location.hostname;
      const currentEnv = getCurrentEnvironment();
      const isSubdomain = isValidSubdomain(hostname, 'landing');
      console.log("🔍 Subdomain detection:", {
        hostname,
        currentEnv,
        isSubdomain,
        expectedSuffix: this.getDomainConfig?.subdomainSuffix,
      });
      return isSubdomain;
    },

    subdomainName() {
      if (!this.isSubdomain) {
        console.log("🔍 No subdomain name - not on subdomain");
        return null;
      }
      const hostname = window.location.hostname;
      const subdomainName = extractSubdomainName(hostname, 'landing');
      console.log("🔍 Subdomain name extracted:", subdomainName);
      return subdomainName;
    },

    // IMPORTANT: Use these effective values throughout the component instead of the original props
    // They handle both regular routes and subdomain routes (landing pages)
    effectiveUserUid() {
      const effective = this.subdomainData?.uid || this.userUid;
      return effective;
    },

    effectiveWid() {
      const effective = this.subdomainData?.wid || this.wid;
      return effective;
    },

    effectiveLocationId() {
      const effective = this.subdomainData?.locationId || this.locationId;
      return effective;
    },

    effectiveSlug() {
      const effective = this.embedSlug || this.slug;
      return effective;
    },

    brandName() {
      // Use location title from database, following StoreLocator logic
      if (this.storeLocation) {
        return this.storeLocation?.gmb_shallow_profile_data
          ? this.storeLocation?.gmb_shallow_profile_data?.title
          : this.storeLocation?.gmbProfile?.title ||
              this.storeLocation.name ||
              this.storeLocation.storeLocation ||
              "Store Brand";
      }
      return this.workspaceData?.name || "Store Brand";
    },
    storeLogo() {
      if (!this.storeLocation) return null;
      
      // Get logo from GMB profile media items
      const gmbProfile = this.storeLocation.gmbProfile;
      if (gmbProfile?.mediaItems?.logo) {
        const logo = gmbProfile.mediaItems.logo;
        // Prefer sourceUrl, fallback to thumbnailUrl
        return logo.thumbnailUrl || logo.sourceUrl;
      }
      
      return null;
    },
    mergedIndividualStoreStyles() {
      // Merge workspace data with any prop styles, workspace data takes precedence
      const workspaceStyles = this.workspaceData?.location_common_styles || {};
      return { ...workspaceStyles };
    },
    
    // CTA Configuration computed properties
    showAppointmentButton() {
      const draftCtaAppointments = this.draftCtaAppointments || {};
      const commonStyles = this.workspaceData?.location_common_styles || {};
      const ctaAppointments = commonStyles?.cta_appointments || {};
      
      // Use draft value if available, otherwise use common styles, default to true
      return draftCtaAppointments.showAppointmentButton !== undefined 
        ? draftCtaAppointments.showAppointmentButton 
        : ctaAppointments.showAppointmentButton !== undefined 
          ? ctaAppointments.showAppointmentButton 
          : true;
    },
    
    showCallButton() {
      const draftCtaAppointments = this.draftCtaAppointments || {};
      const commonStyles = this.workspaceData?.location_common_styles || {};
      const ctaAppointments = commonStyles?.cta_appointments || {};
      
      // Use draft value if available, otherwise use common styles, default to true
      return draftCtaAppointments.showCallButton !== undefined 
        ? draftCtaAppointments.showCallButton 
        : ctaAppointments.showCallButton !== undefined 
          ? ctaAppointments.showCallButton 
          : true;
    },
    
    appointmentButtonText() {
      const draftCtaAppointments = this.draftCtaAppointments || {};
      const commonStyles = this.workspaceData?.location_common_styles || {};
      const ctaAppointments = commonStyles?.cta_appointments || {};
      
      // Use draft value if available, otherwise use common styles, default to "Book An Appointment"
      return draftCtaAppointments.appointmentButtonText || 
             ctaAppointments.appointmentButtonText || 
             "Book An Appointment";
    },
    
    callButtonText() {
      const draftCtaAppointments = this.draftCtaAppointments || {};
      const commonStyles = this.workspaceData?.location_common_styles || {};
      const ctaAppointments = commonStyles?.cta_appointments || {};
      
      // Use draft value if available, otherwise use common styles, default to "Call {{phone}}"
      const text = draftCtaAppointments.callButtonText || 
                   ctaAppointments.callButtonText || 
                   "Call {{phone}}";
      
      // Replace {{phone}} placeholder with actual phone number
      const phone = this.getGmbPhone(this.storeLocation);
      return text.replace('{{phone}}', phone || '');
    },
    
    styleVars() {
      // Get location-specific styles from location_style map (individual store preferences)
      const locationStyles = this.storeLocation?.location_style || {};
      // Get workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles || {};
      // Get header styles from common styles
      const headerStyles = commonStyles?.header_styles || {};

      // Use draft styles for live preview if available
      const draftLocationStyles = this.draftLocationStyle || {};
      const draftHeaderStyles = this.draftHeaderStyle || {};
      const draftHeroSection = this.draftHeroSection || {};
      const draftReviewsSection = this.draftReviewsSection || {};
      const draftContactInformation = this.draftContactInformation || {};
      const draftAppointmentStyles = this.draftAppointmentStyles || {};
      const draftProductStyles = this.draftProductStyles || {};
      const draftPostsSection = this.draftPostsSection || {};
      const draftBlogSection = this.draftBlogSection || {};
      const draftFooterSection = this.draftFooterSection || {};
      const draftSocialSection = this.draftSocialSection || {};
      const draftGallerySection = this.draftGallerySection || {};

      // Helper function to get value with draft preference, then individual store preference
      const getStyleValue = (individualKey, commonKey, defaultValue) => {
        // First check draft styles (for live preview)
        if (draftLocationStyles[individualKey] !== undefined) {
          return draftLocationStyles[individualKey];
        }
        // Then check individual store styles
        if (locationStyles[individualKey] !== undefined) {
          return locationStyles[individualKey];
        }
        // Then check common styles
        if (commonStyles[commonKey] !== undefined) {
          return commonStyles[commonKey];
        }
        // Finally use default
        return defaultValue;
      };

      return {
        // Location styles - individual store takes precedence
        "--store-primary": getStyleValue(
          "primaryColor",
          "primaryColor",
          "#1773EA"
        ),
        "--store-secondary": getStyleValue(
          "secondaryColor",
          "secondaryColor",
          "#1d4ed8"
        ),
        "--store-accent": getStyleValue(
          "accentColor",
          "accentColor",
          "#3b82f6"
        ),
        "--store-background": getStyleValue(
          "backgroundColor",
          "backgroundColor",
          "#ffffff"
        ),
        "--store-text-primary": getStyleValue(
          "textPrimaryColor",
          "textPrimaryColor",
          "#1f2937"
        ),
        "--store-text-secondary": getStyleValue(
          "textSecondaryColor",
          "textSecondaryColor",
          "#6b7280"
        ),
        "--store-font-family": getStyleValue(
          "fontFamily",
          "fontFamily",
          "Inter, sans-serif"
        ),
        "--store-brand-name-size": getStyleValue(
          "brandNameSize",
          "brandNameSize",
          "2rem"
        ),
        "--store-brand-name-color": getStyleValue(
          "brandNameColor",
          "brandNameColor",
          "#0a2540"
        ),
        "--store-button-primary": getStyleValue(
          "buttonPrimaryColor",
          "buttonPrimaryColor",
          "#3b82f6"
        ),
        "--store-button-secondary": getStyleValue(
          "buttonSecondaryColor",
          "buttonSecondaryColor",
          "#ffffff"
        ),
        "--store-button-text": getStyleValue(
          "buttonTextColor",
          "buttonTextColor",
          "#ffffff"
        ),
        "--store-border-color": getStyleValue(
          "borderColor",
          "borderColor",
          "#e5e7eb"
        ),
        "--store-shadow": getStyleValue(
          "shadowColor",
          "shadowColor",
          "rgba(0, 0, 0, 0.1)"
        ),
        "--store-gradient-start": getStyleValue(
          "gradientStart",
          "gradientStart",
          "#3b82f6"
        ),
        "--store-gradient-end": getStyleValue(
          "gradientEnd",
          "gradientEnd",
          "#1d4ed8"
        ),

        // Primary title color for all section titles
        "--primary-title-color": getStyleValue(
          "primaryTitleColor",
          "primaryTitleColor",
          "#1f2937"
        ),

        // Header styles - from draft styles, then common styles
        "--header-logo-size": draftHeaderStyles.logoSize || headerStyles.logoSize || "3rem",
        "--header-title-size": draftHeaderStyles.titleTextSize || headerStyles.titleTextSize || "1.25rem",
        "--header-text-color": draftHeaderStyles.textColor || headerStyles.textColor || "#1f2937",
        "--header-background-color": draftHeaderStyles.backgroundColor || headerStyles.backgroundColor || "#ffffff",

        // Hero Section Styles - from draft styles, then common styles
        "--hero-brand-name-size":
          draftHeroSection.brandNameSize || commonStyles?.hero_section?.brandNameSize || "4rem",
        "--hero-brand-name-color":
          draftHeroSection.brandNameColor || commonStyles?.hero_section?.brandNameColor || "#1f2937",
        "--hero-description-size":
          draftHeroSection.descriptionSize || commonStyles?.hero_section?.descriptionSize || "1.25rem",
        "--hero-description-color":
          draftHeroSection.descriptionColor || commonStyles?.hero_section?.descriptionColor || "#6b7280",
        "--hero-cover-photo-height":
          draftHeroSection.coverPhotoHeight || commonStyles?.hero_section?.coverPhotoHeight || "24rem",
        "--hero-cover-photo-border-radius":
          draftHeroSection.coverPhotoBorderRadius || commonStyles?.hero_section?.coverPhotoBorderRadius || "1rem",
        "--hero-section-bg-color":
          draftHeroSection.backgroundColor || commonStyles?.hero_section?.backgroundColor ||
          "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",

        // Reviews Section Styles - from draft styles, then common styles
        "--reviews-section-bg-color":
          draftReviewsSection.backgroundColor || commonStyles?.reviews_section?.backgroundColor || "#f9fafb",
        "--review-card-bg-color":
          draftReviewsSection.cardBackgroundColor || commonStyles?.reviews_section?.cardBackgroundColor || "#ffffff",
        "--review-card-border-radius":
          draftReviewsSection.cardBorderRadius || commonStyles?.reviews_section?.cardBorderRadius || "1rem",
        "--review-card-border-size":
          draftReviewsSection.cardBorderSize || commonStyles?.reviews_section?.cardBorderSize || "0px",
        "--review-card-text-color":
          draftReviewsSection.cardTextColor || commonStyles?.reviews_section?.cardTextColor || "#1f2937",

        // Contact Information Styles - from draft styles, then common styles
        "--contact-section-bg-color":
          draftContactInformation.backgroundColor || commonStyles?.contact_information?.backgroundColor || "#ffffff",
        "--contact-card-bg-color":
          draftContactInformation.cardBackgroundColor || commonStyles?.contact_information?.cardBackgroundColor || "#f9fafb",
        "--contact-card-border-color":
          draftContactInformation.cardBorderColor || commonStyles?.contact_information?.cardBorderColor || "#e5e7eb",
        "--contact-card-border-radius":
          draftContactInformation.cardBorderRadius || commonStyles?.contact_information?.cardBorderRadius || "0.75rem",

        // Appointment Styles - from draft styles, then common styles
        "--appointment-button-bg-color":
          draftAppointmentStyles.buttonBackgroundColor || commonStyles?.appointment_styles?.buttonBackgroundColor ||
          "linear-gradient(to right, #3b82f6, #1d4ed8)",
        "--appointment-button-text-color":
          draftAppointmentStyles.buttonTextColor || commonStyles?.appointment_styles?.buttonTextColor || "#ffffff",
        "--appointment-button-border-radius":
          draftAppointmentStyles.buttonBorderRadius || commonStyles?.appointment_styles?.buttonBorderRadius || "0.5rem",
        "--appointment-button-border-size":
          draftAppointmentStyles.buttonBorderSize || commonStyles?.appointment_styles?.buttonBorderSize || "0px",
        "--appointment-button-border-color":
          draftAppointmentStyles.buttonBorderColor || commonStyles?.appointment_styles?.buttonBorderColor || "transparent",
        "--appointment-button-hover-bg-color":
          draftAppointmentStyles.buttonHoverBackgroundColor || commonStyles?.appointment_styles?.buttonHoverBackgroundColor ||
          "linear-gradient(to right, #2563eb, #1e40af)",
        "--appointment-button-hover-text-color":
          draftAppointmentStyles.buttonHoverTextColor || commonStyles?.appointment_styles?.buttonHoverTextColor || "#ffffff",
        "--appointment-form-border-radius":
          draftAppointmentStyles.formBorderRadius || commonStyles?.appointment_styles?.formBorderRadius || "1rem",
        "--appointment-section-bg-color":
          draftAppointmentStyles.sectionBackgroundColor || commonStyles?.appointment_styles?.sectionBackgroundColor || "#f9fafb",
        "--appointment-title-color":
          draftAppointmentStyles.titleColor || commonStyles?.appointment_styles?.titleColor || "#1f2937",
        "--appointment-description-color":
          draftAppointmentStyles.descriptionColor || commonStyles?.appointment_styles?.descriptionColor || "#6b7280",
        "--appointment-form-bg-color":
          draftAppointmentStyles.formBackgroundColor || commonStyles?.appointment_styles?.formBackgroundColor || "#ffffff",
        "--appointment-form-title-color":
          draftAppointmentStyles.formTitleColor || commonStyles?.appointment_styles?.formTitleColor || "#1f2937",
        "--appointment-label-color":
          draftAppointmentStyles.labelColor || commonStyles?.appointment_styles?.labelColor || "#374151",

        // Product & Services Styles - from draft styles, then common styles
        "--product-section-bg-color":
          draftProductStyles.sectionBackgroundColor || commonStyles?.product_styles?.sectionBackgroundColor || "#ffffff",
        "--product-card-bg-color":
          draftProductStyles.cardBackgroundColor || commonStyles?.product_styles?.cardBackgroundColor || "#ffffff",
        "--product-card-border-radius":
          draftProductStyles.cardBorderRadius || commonStyles?.product_styles?.cardBorderRadius || "1rem",
        "--product-card-border-size":
          draftProductStyles.cardBorderSize || commonStyles?.product_styles?.cardBorderSize || "1px",
        "--product-card-border-color":
          draftProductStyles.cardBorderColor || commonStyles?.product_styles?.cardBorderColor || "#e5e7eb",
        "--product-image-border-radius":
          draftProductStyles.imageBorderRadius || commonStyles?.product_styles?.imageBorderRadius || "0.75rem",
        
        // Posts Section Styles - from draft styles, then common styles
        "--posts-section-bg-color":
          draftPostsSection?.backgroundColor || commonStyles?.posts_section?.backgroundColor || "#ffffff",
        "--post-card-bg-color":
          draftPostsSection?.cardBackgroundColor || commonStyles?.posts_section?.cardBackgroundColor || "#f9fafb",
        "--post-card-border-color":
          draftPostsSection?.cardBorderColor || commonStyles?.posts_section?.cardBorderColor || "#e5e7eb",
        "--post-card-border-radius":
          draftPostsSection?.cardBorderRadius || commonStyles?.posts_section?.cardBorderRadius || "0.5rem",
        "--post-title-color":
          draftPostsSection?.titleColor || commonStyles?.posts_section?.titleColor || "#1f2937",
        "--post-text-color":
          draftPostsSection?.textColor || commonStyles?.posts_section?.textColor || "#374151",
        "--post-meta-color":
          draftPostsSection?.metaColor || commonStyles?.posts_section?.metaColor || "#6b7280",
        
        // Blog Section Styles - from draft styles, then common styles
        "--blog-section-bg-color":
          draftBlogSection?.backgroundColor || commonStyles?.blog_section?.backgroundColor || "#ffffff",
        "--blog-card-bg-color":
          draftBlogSection?.cardBackgroundColor || commonStyles?.blog_section?.cardBackgroundColor || "#f9fafb",
        "--blog-card-border-color":
          draftBlogSection?.cardBorderColor || commonStyles?.blog_section?.cardBorderColor || "#e5e7eb",
        "--blog-card-border-radius":
          draftBlogSection?.cardBorderRadius || commonStyles?.blog_section?.cardBorderRadius || "0.5rem",
        "--blog-title-color":
          draftBlogSection?.titleColor || commonStyles?.blog_section?.titleColor || "#1f2937",
        "--blog-text-color":
          draftBlogSection?.textColor || commonStyles?.blog_section?.textColor || "#374151",
        "--blog-meta-color":
          draftBlogSection?.metaColor || commonStyles?.blog_section?.metaColor || "#6b7280",
        "--blog-excerpt-color":
          draftBlogSection?.excerptColor || commonStyles?.blog_section?.excerptColor || "#6b7280",
        "--blog-tag-color":
          draftBlogSection?.tagColor || commonStyles?.blog_section?.tagColor || "#3b82f6",
        
        // Footer Styles - from draft styles, then common styles
        "--footer-bg-color":
          draftFooterSection?.backgroundColor || commonStyles?.footer_section?.backgroundColor || "#ffffff",
        "--footer-border-color":
          draftFooterSection?.borderColor || commonStyles?.footer_section?.borderColor || "#e5e7eb",
        "--footer-shadow-color":
          draftFooterSection?.shadowColor || commonStyles?.footer_section?.shadowColor || "rgba(0, 0, 0, 0.1)",
        "--footer-brand-name-color":
          draftFooterSection?.brandNameColor || commonStyles?.footer_section?.brandNameColor || "#1f2937",
        "--footer-text-color":
          draftFooterSection?.textColor || commonStyles?.footer_section?.textColor || "#6b7280",
        "--footer-copyright-color":
          draftFooterSection?.copyrightColor || commonStyles?.footer_section?.copyrightColor || "#6b7280",

        
        // Social Media Section Styles - from draft styles, then common styles
        "--social-section-bg-color":
          draftSocialSection?.backgroundColor || commonStyles?.social_section?.backgroundColor || "#f9fafb",
        "--social-title-color":
          draftSocialSection?.titleColor || commonStyles?.social_section?.titleColor || "#1f2937",
        "--social-subtitle-color":
          draftSocialSection?.subtitleColor || commonStyles?.social_section?.subtitleColor || "#6b7280",
        "--social-icon-color":
          draftSocialSection?.iconColor || commonStyles?.social_section?.iconColor || "#3b82f6",
        "--social-icon-hover-color":
          draftSocialSection?.iconHoverColor || commonStyles?.social_section?.iconHoverColor || "#1d4ed8",
        
        // Social Links Styles - from draft styles, then common styles
        "--social-links-icon-size":
          this.draftSocialLinksStyles?.iconSize || commonStyles?.social_links_styles?.iconSize || "24px",
        "--social-links-icon-color":
          this.draftSocialLinksStyles?.iconColor || commonStyles?.social_links_styles?.iconColor || "#6b7280",
        "--social-links-icon-hover-color":
          this.draftSocialLinksStyles?.iconHoverColor || commonStyles?.social_links_styles?.iconHoverColor || "#3b82f6",
        "--social-links-icon-bg-color":
          this.draftSocialLinksStyles?.iconBackgroundColor || commonStyles?.social_links_styles?.iconBackgroundColor || "transparent",
        "--social-links-icon-border-radius":
          this.draftSocialLinksStyles?.iconBorderRadius || commonStyles?.social_links_styles?.iconBorderRadius || "0.5rem",
        "--social-links-icon-spacing":
          this.draftSocialLinksStyles?.iconSpacing || commonStyles?.social_links_styles?.iconSpacing || "1rem",
        "--social-links-container-padding":
          this.draftSocialLinksStyles?.containerPadding || commonStyles?.social_links_styles?.containerPadding || "1rem",
        
        // Gallery Section Styles - from draft styles, then common styles
        "--gallery-section-bg-color":
          draftGallerySection?.backgroundColor || commonStyles?.gallery_section?.backgroundColor || "#ffffff",
        "--gallery-card-bg-color":
          draftGallerySection?.cardBackgroundColor || commonStyles?.gallery_section?.cardBackgroundColor || "#ffffff",
        "--gallery-card-border-color":
          draftGallerySection?.cardBorderColor || commonStyles?.gallery_section?.cardBorderColor || "#e5e7eb",
        "--gallery-card-border-radius":
          draftGallerySection?.cardBorderRadius || commonStyles?.gallery_section?.cardBorderRadius || "0.5rem",
        "--gallery-title-color":
          draftGallerySection?.titleColor || commonStyles?.gallery_section?.titleColor || "#1f2937",
        "--gallery-subtitle-color":
          draftGallerySection?.subtitleColor || commonStyles?.gallery_section?.subtitleColor || "#6b7280",
        "--gallery-text-color":
          draftGallerySection?.textColor || commonStyles?.gallery_section?.textColor || "#374151",
        "--gallery-image-border-radius":
          draftGallerySection?.imageBorderRadius || commonStyles?.gallery_section?.imageBorderRadius || "0.5rem",
        "--gallery-image-shadow":
          draftGallerySection?.imageShadow || commonStyles?.gallery_section?.imageShadow || "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        "--gallery-card-shadow-color":
          draftGallerySection?.cardShadowColor || commonStyles?.gallery_section?.cardShadowColor || "rgba(0, 0, 0, 0.1)",
        "--gallery-hover-shadow":
          draftGallerySection?.hoverShadow || commonStyles?.gallery_section?.hoverShadow || "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
        "--gallery-overlay-color":
          draftGallerySection?.overlayColor || commonStyles?.gallery_section?.overlayColor || "rgba(0, 0, 0, 0.2)",
        "--gallery-icon-color":
          draftGallerySection?.iconColor || commonStyles?.gallery_section?.iconColor || "#ffffff",
        "--gallery-button-bg-color":
          draftGallerySection?.buttonBackgroundColor || commonStyles?.gallery_section?.buttonBackgroundColor || "#3b82f6",
        "--gallery-button-text-color":
          draftGallerySection?.buttonTextColor || commonStyles?.gallery_section?.buttonTextColor || "#ffffff",
        "--gallery-button-hover-bg-color":
          draftGallerySection?.buttonHoverBackgroundColor || commonStyles?.gallery_section?.buttonHoverBackgroundColor || "#2563eb",
        "--gallery-button-hover-text-color":
          draftGallerySection?.buttonHoverTextColor || commonStyles?.gallery_section?.buttonHoverTextColor || "#ffffff",
      };
    },
    appointments() {
      // First check draft location style (for live preview)
      if (this.draftLocationStyle?.appointments !== undefined) {
        return this.draftLocationStyle.appointments === true;
      }

      // Check individual location settings from database
      if (this.storeLocation?.location_style?.appointments !== undefined) {
        return this.storeLocation.location_style.appointments === true;
      }

      // Default to false if not specified
      return false;
    },
    headerStyles() {
      return this.mergedIndividualStoreStyles?.header_styles || {};
    },

    showHeroSection() {
      return true;
    },
    showProductsSection() {
      return true;
    },
    showReviewsSection() {
      return this.reviews && this.reviews.length > 0;
    },
    showPostsSection() {
      return this.galleryPosts && this.galleryPosts.length > 0;
    },
    showContactSection() {
      return true;
    },
    showAppointmentSection() {
      return this.appointments;
    },
    showSocialMediaSection() {
      return true;
    },
    showBlogsSection() {
      return this.blogs && this.blogs.length > 0;
    },
    showBlogDetailSection() {
      return this.currentBlog !== null;
    },
    showProductDetailSection() {
      return this.currentProduct !== null;
    },
    showGallerySection() {
      return this.galleryImages && this.galleryImages.length > 0;
    },
    showFooter() {
      return true;
    },
    isFormValid() {
      return (
        this.appointmentForm.firstName &&
        this.appointmentForm.lastName &&
        this.appointmentForm.phone &&
        this.appointmentForm.date &&
        this.appointmentForm.time &&
        !this.validationErrors.firstName &&
        !this.validationErrors.lastName &&
        !this.validationErrors.phone &&
        !this.validationErrors.date &&
        !this.validationErrors.time &&
        (!this.appointmentForm.email ||
          this.isValidEmail(this.appointmentForm.email))
      );
    },
    fullStars() {
      if (!this.storeLocation || !this.storeLocation.averageRating) return 0;
      return Math.floor(this.storeLocation.averageRating);
    },
    decimalPart() {
      if (!this.storeLocation || !this.storeLocation.averageRating) return 0;
      return this.storeLocation.averageRating - this.fullStars;
    },
    paginatedReviews() {
      const startIndex = (this.reviewsPage - 1) * this.reviewsPerPage;
      const endIndex = startIndex + this.reviewsPerPage;
      return this.reviews.slice(startIndex, endIndex);
    },
    totalReviewPages() {
      return Math.ceil(this.reviews.length / this.reviewsPerPage);
    },
    hasNextReviewPage() {
      return this.reviewsPage < this.totalReviewPages;
    },
    hasPrevReviewPage() {
      return this.reviewsPage > 1;
    },
    availableServices() {
      // TOP PRIORITY: Get services from workspace-level products_services (admin level)
      if (
        this.workspaceData?.products_services &&
        Array.isArray(this.workspaceData.products_services)
      ) {
        console.log('🔍 [availableServices] Found workspace products_services:', this.workspaceData.products_services);
        const filtered = this.workspaceData.products_services.filter(
          (service) => service && service.name && service.name.trim() !== ""
        );
        if (filtered.length > 0) {
          console.log('✅ [availableServices] Using workspace products_services:', filtered);
          return filtered;
        }
      }

      // Get services from GMB settings data (second priority - this is where BusinessDetails.vue saves data)
      if (
        this.gmbSettingsData?.productsAndServices &&
        Array.isArray(this.gmbSettingsData.productsAndServices)
      ) {
        const filtered = this.gmbSettingsData.productsAndServices.filter(
          (service) => service && service.name && service.name.trim() !== ""
        );
        return filtered;
      }

      // Get services from store location data
      if (!this.storeLocation) {
        return [];
      }

      // Third priority: individual store configuration product_and_services
      if (
        this.storeLocation.product_and_services &&
        Array.isArray(this.storeLocation.product_and_services)
      ) {
        const filtered = this.storeLocation.product_and_services.filter(
          (service) => service && service.name && service.name.trim() !== ""
        );
        return filtered;
      }

      // Second priority: productsAndServices in the location data
      if (
        this.storeLocation.productsAndServices &&
        Array.isArray(this.storeLocation.productsAndServices)
      ) {
        const filtered = this.storeLocation.productsAndServices.filter(
          (service) => service && service.name && service.name.trim() !== ""
        );
        return filtered;
      }

      // Check for services in GMB profile data
      if (
        this.storeLocation.gmbProfile?.productsAndServices &&
        Array.isArray(this.storeLocation.gmbProfile.productsAndServices)
      ) {
        const filtered =
          this.storeLocation.gmbProfile.productsAndServices.filter(
            (service) => service && service.name && service.name.trim() !== ""
          );
        return filtered;
      }

      // Check for services in shallow profile data
      if (
        this.storeLocation.gmb_shallow_profile_data?.productsAndServices &&
        Array.isArray(
          this.storeLocation.gmb_shallow_profile_data.productsAndServices
        )
      ) {
        const filtered =
          this.storeLocation.gmb_shallow_profile_data.productsAndServices.filter(
            (service) => service && service.name && service.name.trim() !== ""
          );
        return filtered;
      }

      // Check for services in workspace data (lowest priority)
      if (
        this.workspaceData?.gmb_details?.productsAndServices &&
        Array.isArray(this.workspaceData.gmb_details.productsAndServices)
      ) {
        const filtered =
          this.workspaceData.gmb_details.productsAndServices.filter(
            (service) => service && service.name && service.name.trim() !== ""
          );
        return filtered;
      }

      return [];
    },
    displayedServices() {
      // Show only first 6 services in home section, all services in products section
      if (this.currentSection === 'products') {
        return this.availableServices;
      }
      return this.availableServices.slice(0, 6);
    },
    socialMediaLinks() {
      const links = [];

      // First priority: individual store configuration social_links
      if (this.storeLocation?.social_links) {
        Object.entries(this.storeLocation.social_links).forEach(
          ([platform, url]) => {
            if (url && url.trim() !== "") {
              const platformConfig = this.getSocialPlatformConfig(platform);
              if (platformConfig) {
                links.push({
                  platform: platformConfig.name,
                  url: url,
                  icon: platformConfig.icon,
                  iconColor: platformConfig.iconColor,
                });
              }
            }
          }
        );
      }

      // Second priority: locationid.gmbProfile.socialInfo.links (only if no links found from first source)
      if (
        links.length === 0 &&
        this.storeLocation?.gmbProfile?.socialInfo?.links &&
        Array.isArray(this.storeLocation.gmbProfile.socialInfo.links)
      ) {
        this.storeLocation.gmbProfile.socialInfo.links.forEach((link) => {
          if (link.url && link.platform) {
            const platformConfig = this.getSocialPlatformConfig(link.platform);
            if (platformConfig) {
              links.push({
                platform: platformConfig.name,
                url: link.url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor,
              });
            }
          }
        });
      }

      // Third priority: workspace social_links (only if no links found from first two sources)
      if (links.length === 0 && this.workspaceData?.social_links) {
        Object.entries(this.workspaceData.social_links).forEach(
          ([platform, url]) => {
            if (url && url.trim() !== "") {
              const platformConfig = this.getSocialPlatformConfig(platform);
              if (platformConfig) {
                links.push({
                  platform: platformConfig.name,
                  url: url,
                  icon: platformConfig.icon,
                  iconColor: platformConfig.iconColor,
                });
              }
            }
          }
        );
      }

      return links;
    },

    // SEO Computed Properties
    seoPageTitle() {
      if (!this.storeLocation) return "Store Page";

      const businessName = this.brandName;
      const locationName =
        this.storeLocation?.name || this.storeLocation?.storeLocation || "";
      const city = this.storeLocation?.city || "";
      const state = this.storeLocation?.state || "";

      let title = businessName;
      if (locationName && locationName !== businessName) {
        title += ` - ${locationName}`;
      }
      if (city && state) {
        title += ` | ${city}, ${state}`;
      } else if (city) {
        title += ` | ${city}`;
      }

      return title;
    },

    seoMetaDescription() {
      if (!this.storeLocation)
        return "Visit our store for quality products and services.";

      const businessName = this.brandName;
      const description = this.getBusinessDescription(this.storeLocation);
      const city = this.storeLocation?.city || "";
      const state = this.storeLocation?.state || "";
      const services = this.availableServices
        .slice(0, 3)
        .map((s) => s.name)
        .join(", ");

      let metaDesc = "";

      if (description) {
        metaDesc = description;
      } else {
        metaDesc = `Visit ${businessName}`;
        if (city && state) {
          metaDesc += ` in ${city}, ${state}`;
        } else if (city) {
          metaDesc += ` in ${city}`;
        }
        metaDesc += " for quality products and services.";
      }

      if (services) {
        metaDesc += ` We offer ${services} and more.`;
      }

      if (this.storeLocation?.averageRating) {
        metaDesc += ` Rated ${this.storeLocation.averageRating.toFixed(
          1
        )} stars by our customers.`;
      }

      // Limit to 160 characters for optimal SEO
      return metaDesc.length > 160
        ? metaDesc.substring(0, 157) + "..."
        : metaDesc;
    },

    seoKeywords() {
      if (!this.storeLocation) return "store, business, services";

      const keywords = [];

      // Business name
      keywords.push(this.brandName);

      // Location keywords
      if (this.storeLocation?.city) keywords.push(this.storeLocation.city);
      if (this.storeLocation?.state) keywords.push(this.storeLocation.state);
      if (this.storeLocation?.zipcode)
        keywords.push(this.storeLocation.zipcode);

      // Service keywords
      this.availableServices.forEach((service) => {
        if (service.name) keywords.push(service.name);
      });

      // Industry-specific keywords based on business type
      const businessType = this.getBusinessType();
      if (businessType) {
        keywords.push(businessType);
      }

      // Common business keywords
      keywords.push("business", "store", "services", "local business");

      return keywords.join(", ");
    },

    seoImageUrl() {
      // Priority: Cover photo, then logo, then default
      const coverPhoto = this.getCoverPhoto(this.storeLocation);
      if (coverPhoto) return coverPhoto;

      if (this.storeLogo) return this.storeLogo;

      // Default business image
      return (
        "https://via.placeholder.com/1200x630/3b82f6/ffffff?text=" +
        encodeURIComponent(this.brandName)
      );
    },

    currentPageUrl() {
      return window.location.href;
    },

    seoGeoRegion() {
      if (!this.storeLocation?.state) return "";
      return this.storeLocation.state;
    },

    seoGeoPlaceName() {
      if (!this.storeLocation?.city) return "";
      return this.storeLocation.city;
    },

    seoGeoPosition() {
      if (this.apifyLat && this.apifyLng) {
        return `${this.apifyLat};${this.apifyLng}`;
      }
      return "";
    },

    schemaMarkup() {
      if (!this.storeLocation) return "";

      const schema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: this.brandName,
        description:
          this.getBusinessDescription(this.storeLocation) ||
          `Visit ${this.brandName} for quality products and services.`,
        url: this.currentPageUrl,
        telephone: this.getGmbPhone(this.storeLocation),
        email: this.storeLocation?.email,
        address: {
          "@type": "PostalAddress",
          streetAddress:
            this.storeLocation?.address ||
            this.getFullAddress(this.storeLocation),
          addressLocality: this.storeLocation?.city,
          addressRegion: this.storeLocation?.state,
          postalCode: this.storeLocation?.zipcode,
          addressCountry: this.storeLocation?.country || "US",
        },
        geo:
          this.apifyLat && this.apifyLng
            ? {
                "@type": "GeoCoordinates",
                latitude: this.apifyLat,
                longitude: this.apifyLng,
              }
            : undefined,
        image: this.seoImageUrl,
        logo: this.storeLogo,
        sameAs: this.socialMediaLinks.map((link) => link.url),
        openingHours: this.getOpeningHours(this.storeLocation).map(
          (hours) => `${hours.day} ${hours.hours}`
        ),
        priceRange: "$$",
        paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
        currenciesAccepted: "USD",
      };

      // Add aggregate rating if available
      if (
        this.storeLocation?.averageRating &&
        this.storeLocation?.reviewCount
      ) {
        schema.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: this.storeLocation.averageRating,
          reviewCount: this.storeLocation.reviewCount,
          bestRating: 5,
          worstRating: 1,
        };
      }

      // Add reviews if available
      if (this.reviews && this.reviews.length > 0) {
        schema.review = this.reviews.slice(0, 5).map((review) => ({
          "@type": "Review",
          author: {
            "@type": "Person",
            name: review.displayName,
          },
          reviewRating: {
            "@type": "Rating",
            ratingValue: review.starRating,
            bestRating: 5,
            worstRating: 1,
          },
          reviewBody: review.comment,
          datePublished: review.updateTime,
        }));
      }

      // Add services if available
      if (this.availableServices.length > 0) {
        schema.hasOfferCatalog = {
          "@type": "OfferCatalog",
          name: "Services",
          itemListElement: this.availableServices.map((service, index) => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: service.name,
              description: service.description,
            },
            price: service.price,
            priceCurrency: "USD",
          })),
        };
      }

      return JSON.stringify(schema);
    },
    currentGalleryImage() {
      if (this.galleryImages.length === 0 || this.currentGalleryIndex < 0 || this.currentGalleryIndex >= this.galleryImages.length) {
        return null;
      }
      return this.galleryImages[this.currentGalleryIndex];
    },
    displayedGalleryImages() {
      // In home section, show limited images. In gallery section, show all images
      if (this.currentSection === 'home' || this.currentSection === '') {
        return this.galleryImages.slice(0, this.maxGalleryImages);
      }
      return this.galleryImages;
    },
  },
  watch: {
    workspaceData: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal) {
          this.updateFavicon();
          this.updatePageTitle();
        }
      },
    },
    brandName: {
      immediate: true,
      handler(newVal, oldVal) {
        if (newVal && newVal !== "Store Brand") {
          this.updatePageTitle();
        }
      },
    },
    // Watch for data changes to update URL hash handling
    storeLocation: {
      handler() {
        this.handleUrlHash();
        this.updateMetaTags();
      },
    },
    galleryPosts: {
      handler() {
        this.handleUrlHash();
      },
    },
    // Watch for loading state changes to update meta tags
    isLoading: {
      handler(newVal) {
        if (!newVal && this.storeLocation) {
          this.$nextTick(() => {
            this.updateMetaTags();
          });
        }
      },
    },
  },
  methods: {
    getDomainConfig() {
      return getDomainConfig();
    },
    generateSlug(text) {
      if (!text) return "";
      return text
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    },

    async fetchSubdomainData() {
      console.log("[fetchSubdomainData] Starting subdomain data fetch...");
      console.log("[fetchSubdomainData] Is subdomain:", this.isSubdomain);
      console.log("[fetchSubdomainData] Subdomain name:", this.subdomainName);
      console.log(
        "[fetchSubdomainData] Current hostname:",
        window.location.hostname
      );
      console.log("[fetchSubdomainData] Firebase db instance:", db);
      console.log(
        "[fetchSubdomainData] Firebase project ID:",
        db.app.options.projectId
      );

      if (!this.isSubdomain || !this.subdomainName) {
        console.log(
          "[fetchSubdomainData] Not a subdomain or no subdomain name"
        );
        return;
      }

      this.isSubdomainLoading = true;
      console.log(
        "[fetchSubdomainData] Fetching data for subdomain:",
        this.subdomainName
      );

      try {
        console.log(
          "[fetchSubdomainData] Using direct document access approach..."
        );
        // Use direct document access like StoreLocator.vue does
        const domainRef = doc(db, "unique_domain", this.subdomainName);
        console.log(
          "[fetchSubdomainData] Document reference created:",
          domainRef
        );
        console.log("[fetchSubdomainData] Document path:", domainRef.path);
        console.log("[fetchSubdomainData] Document ID:", domainRef.id);

        console.log("[fetchSubdomainData] Executing document fetch...");
        const startTime = Date.now();
        const docSnap = await getDoc(domainRef);
        const endTime = Date.now();
        console.log(
          "[fetchSubdomainData] Document fetch completed in",
          endTime - startTime,
          "ms"
        );
        console.log("[fetchSubdomainData] Document exists:", docSnap.exists());
        console.log(
          "[fetchSubdomainData] Document metadata:",
          docSnap.metadata
        );

        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("[fetchSubdomainData] Found subdomain document:", {
            id: docSnap.id,
            data: data,
            dataKeys: Object.keys(data),
          });

          // Validate that uid and wid are present
          if (data.uid && data.wid) {
            this.subdomainData = {
              uid: data.uid,
              wid: data.wid,
              locationId: data.locationId,
            };

            console.log(
              "[fetchSubdomainData] Set subdomain data:",
              this.subdomainData
            );
            console.log("[fetchSubdomainData] UID:", data.uid);
            console.log("[fetchSubdomainData] WID:", data.wid);
            console.log("[fetchSubdomainData] LocationId:", data.locationId);
          } else {
            console.error(
              "[fetchSubdomainData] Subdomain data missing uid or wid"
            );
            console.error("[fetchSubdomainData] Available data:", data);
            console.error("[fetchSubdomainData] UID type:", typeof data.uid);
            console.error("[fetchSubdomainData] WID type:", typeof data.wid);
            throw new Error(
              "Invalid subdomain configuration - missing uid or wid"
            );
          }
        } else {
          console.error(
            "[fetchSubdomainData] Subdomain not found in unique_domain collection"
          );
          console.error(
            "[fetchSubdomainData] Subdomain name:",
            this.subdomainName
          );
          console.error(
            "[fetchSubdomainData] Collection path:",
            "unique_domain"
          );
          console.error(
            "[fetchSubdomainData] Document ID:",
            this.subdomainName
          );

          // Let's also check what documents exist in the collection
          try {
            console.log(
              "[fetchSubdomainData] Checking what documents exist in unique_domain collection..."
            );
            const allDocsQuery = query(collection(db, "unique_domain"));
            const allDocsSnapshot = await getDocs(allDocsQuery);
            console.log(
              "[fetchSubdomainData] Total documents in unique_domain collection:",
              allDocsSnapshot.docs.length
            );

            const availableDocs = allDocsSnapshot.docs.map((doc) => ({
              id: doc.id,
              data: doc.data(),
            }));
            console.error(
              "[fetchSubdomainData] Available documents in collection:",
              availableDocs
            );
          } catch (listError) {
            console.error(
              "[fetchSubdomainData] Could not list available documents:",
              listError
            );
          }

          throw new Error(
            `Subdomain '${this.subdomainName}' not found in unique_domain collection`
          );
        }
      } catch (error) {
        console.error(
          "[fetchSubdomainData] Error fetching subdomain data:",
          error
        );
        console.error("[fetchSubdomainData] Error details:", {
          message: error.message,
          code: error.code,
          name: error.name,
          stack: error.stack,
        });

        // Check if it's a Firebase connection error
        if (error.code === "unavailable" || error.message.includes("offline")) {
          console.error(
            "[fetchSubdomainData] Firebase connection issue detected"
          );
          console.error(
            "[fetchSubdomainData] This appears to be a network/Firebase connectivity problem"
          );
          console.error(
            "[fetchSubdomainData] Check your internet connection and Firebase configuration"
          );
        }

        // Check if it's a permissions error
        if (error.code === "permission-denied") {
          console.error(
            "[fetchSubdomainData] Firebase permissions issue detected"
          );
          console.error(
            "[fetchSubdomainData] Check your Firebase security rules for the unique_domain collection"
          );
        }

        // Check if it's a not-found error
        if (error.code === "not-found") {
          console.error("[fetchSubdomainData] Document not found in Firebase");
          console.error(
            "[fetchSubdomainData] Ensure the document exists in the unique_domain collection"
          );
        }

        this.subdomainData = null;
      } finally {
        console.log("[fetchSubdomainData] Setting loading to false");
        this.isSubdomainLoading = false;
      }
    },

    async fetchStoreData() {
      console.log("🚀 [fetchStoreData] Starting data fetch process...");
      console.log("🚀 [fetchStoreData] Initial props:", {
        userUid: this.userUid,
        wid: this.wid,
        locationId: this.locationId,
        slug: this.slug,
        embedSlug: this.embedSlug,
      });
      console.log("🚀 [fetchStoreData] Effective values:", {
        effectiveUserUid: this.effectiveUserUid,
        effectiveWid: this.effectiveWid,
        effectiveLocationId: this.effectiveLocationId,
        effectiveSlug: this.effectiveSlug,
      });
      console.log("🚀 [fetchStoreData] Is subdomain:", this.isSubdomain);
      console.log("🚀 [fetchStoreData] Subdomain name:", this.subdomainName);

      this.isLoading = true;

      // If this is a subdomain, fetch subdomain data first
      if (this.isSubdomain) {
        console.log("[fetchStoreData] Fetching subdomain data...");
        await this.fetchSubdomainData();
        console.log(
          "[fetchStoreData] Subdomain data fetched:",
          this.subdomainData
        );
      }

      // Use effective values (subdomain data or props)
      const userUid = this.effectiveUserUid;
      const wid = this.effectiveWid;
      const locationId = this.effectiveLocationId;

      console.log("📋 [fetchStoreData] Effective values:", {
        userUid,
        wid,
        locationId,
        effectiveSlug: this.effectiveSlug,
      });

      // Validate props before making Firestore calls
      if (!userUid || !wid || !locationId) {
        console.error("❌ [fetchStoreData] Missing required props:", {
          userUid,
          wid,
          locationId,
        });
        console.error(
          "❌ [fetchStoreData] Subdomain data:",
          this.subdomainData
        );
        console.error("❌ [fetchStoreData] Original props:", {
          userUid: this.userUid,
          wid: this.wid,
          locationId: this.locationId,
        });
        console.error("❌ [fetchStoreData] Effective values:", {
          effectiveUserUid: this.effectiveUserUid,
          effectiveWid: this.effectiveWid,
          effectiveLocationId: this.effectiveLocationId,
        });
        this.isLoading = false;
        return;
      }

      try {
        console.log("📂 [fetchStoreData] Fetching workspace data...");
        console.log(
          "📂 [fetchStoreData] Workspace path: users/" +
            userUid +
            "/fly_workspaces/" +
            wid
        );

        // Fetch workspace data from /users/uid/fly_workspaces/wid
        const workspaceRef = doc(db, "users", userUid, "fly_workspaces", wid);
        console.log(
          "📂 [fetchStoreData] Workspace reference created:",
          workspaceRef
        );

        const workspaceDoc = await getDoc(workspaceRef);
        console.log(
          "📂 [fetchStoreData] Workspace document exists:",
          workspaceDoc.exists()
        );

        if (workspaceDoc.exists()) {
          this.workspaceData = workspaceDoc.data();
          console.log("✅ [fetchStoreData] Workspace data loaded:", {
            name: this.workspaceData?.name,
            location_common_styles: this.workspaceData?.location_common_styles
              ? "Present"
              : "Missing",
            products_services: this.workspaceData?.products_services
              ? `Present (${this.workspaceData.products_services.length} items)`
              : "Missing",
          });
          if (this.workspaceData?.products_services) {
            console.log("📦 [fetchStoreData] Products/Services data:", this.workspaceData.products_services);
          }
        } else {
          console.warn("⚠️ [fetchStoreData] Workspace document does not exist");
          this.workspaceData = null;
        }

        console.log("🔧 [fetchStoreData] Fetching GMB settings data...");
        // Fetch GMB settings data
        await this.fetchGMBSettingsData();

        console.log("📍 [fetchStoreData] Fetching location data...");
        console.log(
          "📍 [fetchStoreData] Location path: users/" +
            userUid +
            "/fly_workspaces/" +
            locationId
        );

        // Fetch location data from /users/uid/fly_workspaces/location-id
        const locationRef = doc(
          db,
          "users",
          userUid,
          "fly_workspaces",
          locationId
        );
        console.log(
          "📍 [fetchStoreData] Location reference created:",
          locationRef
        );

        const locationDoc = await getDoc(locationRef);
        console.log(
          "📍 [fetchStoreData] Location document exists:",
          locationDoc.exists()
        );

        if (locationDoc.exists()) {
          this.storeLocation = locationDoc.data();
          console.log("✅ [fetchStoreData] Location data loaded:", {
            name: this.storeLocation?.name,
            storeLocation: this.storeLocation?.storeLocation,
            city: this.storeLocation?.city,
            state: this.storeLocation?.state,
            gmbProfile: this.storeLocation?.gmbProfile ? "Present" : "Missing",
            gmbLogo: this.storeLocation?.gmbProfile?.mediaItems?.logo ? "Present" : "Missing",
            gmb_shallow_profile_data: this.storeLocation
              ?.gmb_shallow_profile_data
              ? "Present"
              : "Missing",
            product_and_services: this.storeLocation?.product_and_services
              ? "Present"
              : "Missing",
            social_links: this.storeLocation?.social_links
              ? "Present"
              : "Missing",
            location_style: this.storeLocation?.location_style
              ? "Present"
              : "Missing",
          });

          // Fetch reviews for this location
          this.fetchReviewsForLocation(this.storeLocation);

          // Fetch gallery posts for this location
          this.fetchGalleryPosts();

          // Fetch blogs for this workspace
          this.fetchBlogs();

          // Fetch gallery images for this location
          this.fetchGalleryImages();
        } else {
          console.warn("⚠️ [fetchStoreData] Location document does not exist");
          this.storeLocation = null;
        }

        // Optionally fetch Apify lat/lng if needed
        if (this.storeLocation) {
          try {
            const apifyColRef = collection(
              db,
              "users",
              userUid,
              "fly_workspaces",
              locationId,
              "gmb_locations_apify_profiles"
            );

            const apifyColSnap = await getDocs(apifyColRef);

            for (const docSnap of apifyColSnap.docs) {
              const data = docSnap.data();
              if (
                data &&
                data.location &&
                typeof data.location.lat === "number" &&
                typeof data.location.lng === "number"
              ) {
                this.apifyLat = data.location.lat;
                this.apifyLng = data.location.lng;
                break;
              }
            }
          } catch (apifyError) {
            console.warn("Could not fetch Apify data:", apifyError);
          }
        }
      } catch (e) {
        console.error("Error fetching store data:", e);
        this.storeLocation = null;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchGMBSettingsData() {
      try {
        const gmbSettingsCollectionRef = collection(
          db,
          "users",
          this.effectiveUserUid,
          "fly_workspaces",
          this.effectiveWid,
          "gmb-settings"
        );

        const latestDocQuery = query(
          gmbSettingsCollectionRef,
          orderBy("created_at", "desc"),
          limit(1)
        );

        const querySnapshot = await getDocs(latestDocQuery);

        if (!querySnapshot.empty) {
          const latestDoc = querySnapshot.docs[0];
          const docData = latestDoc.data();

          const gmbData = docData.gmb_details;

          this.gmbSettingsData = gmbData;
        } else {
          this.gmbSettingsData = null;
        }
      } catch (error) {
        console.error("Error fetching GMB settings:", error);
        this.gmbSettingsData = null;
      }
    },
    getFullAddress(location) {
      if (!location) return "";
      if (location.address) return location.address;
      if (location.gmbProfile?.storefrontAddress?.addressLines?.length) {
        return location.gmbProfile.storefrontAddress.addressLines.join(", ");
      }
      if (location.gmbProfile?.address) return location.gmbProfile.address;
      const parts = [
        location.name || location.storeLocation,
        location.sub_locality,
        location.city,
        location.state,
        location.zipcode,
        location.country,
      ].filter(Boolean);
      return parts.join(", ");
    },
    getBusinessDescription(location) {
      return (
        location?.gmbProfile?.profile?.description ||
        location?.gmb_shallow_profile_data?.description ||
        null
      );
    },
    getProductsServicesSectionName() {
      // Get section name from GMB settings data (highest priority)
      if (this.gmbSettingsData?.productsServicesSectionName) {
        return this.gmbSettingsData.productsServicesSectionName;
      }
      // Default fallback
      return "Products & Services";
    },
    getProductsServicesSectionSubtext() {
      // Get section subtext from GMB settings data (highest priority)
      if (this.gmbSettingsData?.productsServicesSectionSubtext) {
        return this.gmbSettingsData.productsServicesSectionSubtext;
      }
      // Default fallback
      return "Explore our comprehensive range of products and services";
    },
    // Section Headers Configuration
    getReviewsSectionTitle() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.reviewsTitle) {
        return this.draftSectionHeaders.reviewsTitle;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.reviewsTitle) {
        return commonStyles.reviewsTitle;
      }
      // Default fallback
      return "What Our Customers Say";
    },
    getReviewsSectionSubtext() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.reviewsSubtext) {
        return this.draftSectionHeaders.reviewsSubtext;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.reviewsSubtext) {
        return commonStyles.reviewsSubtext;
      }
      // Default fallback
      return "Read testimonials from satisfied customers";
    },
    getAppointmentSectionTitle() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.appointmentTitle) {
        return this.draftSectionHeaders.appointmentTitle;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.appointmentTitle) {
        return commonStyles.appointmentTitle;
      }
      // Default fallback
      return "Book Your Appointment";
    },
    getAppointmentSectionSubtext() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.appointmentSubtext) {
        return this.draftSectionHeaders.appointmentSubtext;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.appointmentSubtext) {
        return commonStyles.appointmentSubtext;
      }
      // Default fallback
      return "Take the first step towards better service! Our team is here to help you.";
    },
    getContactSectionTitle() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.contactTitle) {
        return this.draftSectionHeaders.contactTitle;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.contactTitle) {
        return commonStyles.contactTitle;
      }
      // Default fallback
      return "Contact Information";
    },
    getContactSectionSubtext() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.contactSubtext) {
        return this.draftSectionHeaders.contactSubtext;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.contactSubtext) {
        return commonStyles.contactSubtext;
      }
      // Default fallback
      return "Get in touch with us. We're here to help you with all your needs.";
    },
    getPostsSectionTitle() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.postsTitle) {
        return this.draftSectionHeaders.postsTitle;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.postsTitle) {
        return commonStyles.postsTitle;
      }
      // Default fallback
      return "Our Latest Posts";
    },
    getPostsSectionSubtext() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.postsSubtext) {
        return this.draftSectionHeaders.postsSubtext;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.postsSubtext) {
        return commonStyles.postsSubtext;
      }
      // Default fallback
      return "Check out our recent updates and content";
    },
    getBlogSectionTitle() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.blogTitle) {
        return this.draftSectionHeaders.blogTitle;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.blogTitle) {
        return commonStyles.blogTitle;
      }
      // Default fallback
      return "Latest Blog Posts";
    },
    getBlogSectionSubtext() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.blogSubtext) {
        return this.draftSectionHeaders.blogSubtext;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.blogSubtext) {
        return commonStyles.blogSubtext;
      }
      // Default fallback
      return "Read our latest articles and insights";
    },
    getGallerySectionTitle() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.galleryTitle) {
        return this.draftSectionHeaders.galleryTitle;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.galleryTitle) {
        return commonStyles.galleryTitle;
      }
      // Default fallback
      return "Photo Gallery";
    },
    getGallerySectionSubtext() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.gallerySubtext) {
        return this.draftSectionHeaders.gallerySubtext;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.gallerySubtext) {
        return commonStyles.gallerySubtext;
      }
      // Default fallback
      return "Browse through our collection of photos";
    },
    getSocialSectionTitle() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.socialTitle) {
        return this.draftSectionHeaders.socialTitle;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.socialTitle) {
        return commonStyles.socialTitle;
      }
      // Default fallback
      return "Find us on social media";
    },
    getSocialSectionSubtext() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.socialSubtext) {
        return this.draftSectionHeaders.socialSubtext;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.socialSubtext) {
        return commonStyles.socialSubtext;
      }
      // Default fallback
      return "Connect with us and stay updated with our latest news and updates";
    },
    getProductsSectionTitle() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.productsTitle) {
        return this.draftSectionHeaders.productsTitle;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.productsTitle) {
        return commonStyles.productsTitle;
      }
      // Use existing method as fallback
      return this.getProductsServicesSectionName();
    },
    getProductsSectionSubtext() {
      // Get from draft config (highest priority)
      if (this.draftSectionHeaders?.productsSubtext) {
        return this.draftSectionHeaders.productsSubtext;
      }
      // Get from workspace common styles
      const commonStyles = this.workspaceData?.location_common_styles?.section_headers;
      if (commonStyles?.productsSubtext) {
        return commonStyles.productsSubtext;
      }
      // Use existing method as fallback
      return this.getProductsServicesSectionSubtext();
    },
    getCoverPhoto(location) {
      if (location?.gmb_shallow_profile_data?.cover_photo)
        return location.gmb_shallow_profile_data.cover_photo;
      if (location?.gmbProfile?.mediaItems?.coverPhoto?.googleUrl)
        return location.gmbProfile.mediaItems.coverPhoto.googleUrl;
      return null;
    },
    getSocialLinks(location) {
      const socialLinks = [];
      if (location?.gmb_attributes && Array.isArray(location.gmb_attributes)) {
        const socialPlatforms = {
          "attributes/url_facebook": {
            platform: "Facebook",
            icon: "mdi-facebook",
            iconColor: "#1877F2",
          },
          "attributes/url_instagram": {
            platform: "Instagram",
            icon: "mdi-instagram",
            iconColor: "#E4405F",
          },
          "attributes/url_twitter": {
            platform: "Twitter",
            icon: "mdi-twitter",
            iconColor: "#1DA1F2",
          },
          "attributes/url_linkedin": {
            platform: "LinkedIn",
            icon: "mdi-linkedin",
            iconColor: "#0A66C2",
          },
          "attributes/url_pinterest": {
            platform: "Pinterest",
            icon: "mdi-pinterest",
            iconColor: "#BD081C",
          },
          "attributes/url_youtube": {
            platform: "YouTube",
            icon: "mdi-youtube",
            iconColor: "#FF0000",
          },
        };
        location.gmb_attributes.forEach((attr) => {
          if (socialPlatforms[attr.name] && attr.value) {
            socialLinks.push({
              platform: socialPlatforms[attr.name].platform,
              url: attr.value,
              icon: socialPlatforms[attr.name].icon,
              iconColor: socialPlatforms[attr.name].iconColor,
            });
          }
        });
      }
      if (
        location?.gmb_shallow_profile_data?.social_links &&
        Array.isArray(location.gmb_shallow_profile_data.social_links)
      ) {
        location.gmb_shallow_profile_data.social_links.forEach((link) => {
          if (link.url && link.platform) {
            const platformConfig = {
              facebook: { icon: "mdi-facebook", iconColor: "#1877F2" },
              instagram: { icon: "mdi-instagram", iconColor: "#E4405F" },
              twitter: { icon: "mdi-twitter", iconColor: "#1DA1F2" },
              linkedin: { icon: "mdi-linkedin", iconColor: "#0A66C2" },
              pinterest: { icon: "mdi-pinterest", iconColor: "#BD081C" },
              youtube: { icon: "mdi-youtube", iconColor: "#FF0000" },
            };
            const config = platformConfig[link.platform.toLowerCase()];
            if (config) {
              socialLinks.push({
                platform:
                  link.platform.charAt(0).toUpperCase() +
                  link.platform.slice(1),
                url: link.url,
                icon: config.icon,
                iconColor: config.iconColor,
              });
            }
          }
        });
      }
      return socialLinks;
    },
    getOpeningHours(location) {
      if (location?.gmb_shallow_profile_data?.opening_hours) {
        return location.gmb_shallow_profile_data.opening_hours.map((hours) => ({
          day: this.formatDayName(hours.day),
          hours: hours.hours,
        }));
      }
      if (location?.gmbProfile?.regularHours?.periods) {
        const daysMap = {
          MONDAY: "Monday",
          TUESDAY: "Tuesday",
          WEDNESDAY: "Wednesday",
          THURSDAY: "Thursday",
          FRIDAY: "Friday",
          SATURDAY: "Saturday",
          SUNDAY: "Sunday",
        };
        return location.gmbProfile.regularHours.periods.map((period) => ({
          day: daysMap[period.openDay] || period.openDay,
          hours:
            period.openTime && period.closeTime
              ? `${period.openTime.hours}:${
                  period.openTime.minutes?.toString().padStart(2, "0") || "00"
                } - ${period.closeTime.hours}:${
                  period.closeTime.minutes?.toString().padStart(2, "0") || "00"
                }`
              : "Closed",
        }));
      }
      return [];
    },
    formatDayName(day) {
      const daysMap = {
        monday: "Monday",
        tuesday: "Tuesday",
        wednesday: "Wednesday",
        thursday: "Thursday",
        friday: "Friday",
        saturday: "Saturday",
        sunday: "Sunday",
      };
      return daysMap[day?.toLowerCase()] || day;
    },
    formatWebsite(website) {
      if (!website) return "";
      return website.replace(/^https?:\/\//, "").replace(/^www\./, "");
    },
    isCurrentDay(day) {
      const today = new Date().toLocaleDateString("en-US", { weekday: "long" });
      return day?.toLowerCase() === today.toLowerCase();
    },
    getGoogleMapsSrc() {
      if (this.apifyLat && this.apifyLng) {
        return `https://www.google.com/maps?q=${this.apifyLat},${this.apifyLng}&output=embed`;
      }
      if (this.storeLocation) {
        const addrParts = [
          this.storeLocation.name || this.storeLocation.storeLocation,
          this.getFullAddress(this.storeLocation),
        ].filter(Boolean);
        const addr = addrParts.join(", ");
        return `https://www.google.com/maps?q=${encodeURIComponent(
          addr
        )}&output=embed`;
      }
      return "";
    },

    testDirectionsUrl() {
      console.log('[testDirectionsUrl] Testing directions URL generation...');
      const url = this.directionsUrl;
      console.log('[testDirectionsUrl] Generated URL:', url);
      
      if (url) {
        // Test opening the URL
        window.open(url, '_blank');
        console.log('[testDirectionsUrl] Opened URL in new tab');
      } else {
        console.log('[testDirectionsUrl] No URL generated');
      }
    },
    testAddressConstruction() {
      console.log('[testAddressConstruction] Testing address construction...');
      console.log('[testAddressConstruction] storeLocation:', this.storeLocation);
      
      if (this.storeLocation) {
        const fullAddress = this.getFullAddress(this.storeLocation);
        console.log('[testAddressConstruction] Full address:', fullAddress);
        
        const addrParts = [
          this.storeLocation.name || this.storeLocation.storeLocation,
          fullAddress,
        ].filter(Boolean);
        
        console.log('[testAddressConstruction] Address parts:', addrParts);
        const addr = addrParts.join(", ");
        console.log('[testAddressConstruction] Combined address:', addr);
        console.log('[testAddressConstruction] Address trimmed:', addr.trim());
        console.log('[testAddressConstruction] Address length:', addr.trim().length);
        
        if (addr.trim()) {
          const url = `https://www.google.com/maps/dir//${encodeURIComponent(addr)}`;
          console.log('[testAddressConstruction] Generated URL:', url);
          
          // Test opening the URL
          window.open(url, '_blank');
          console.log('[testAddressConstruction] Opened URL in new tab');
          return url;
        } else {
          console.log('[testAddressConstruction] Address is empty after trimming');
        }
      }
      
      console.log('[testAddressConstruction] No address available');
      return "";
    },
    async fetchReviewsForLocation(location) {
      this.isReviewsLoading = true;
      this.reviews = [];
      // Reset pagination when fetching new reviews
      this.reviewsPage = 1;

      try {
        // Prepare account info for the selected location
        const currentUID = this.effectiveUserUid;
        const wid = this.effectiveLocationId;

        // Get workspace details
        const workspaceRef = doc(
          db,
          "users",
          currentUID,
          "fly_workspaces",
          wid
        );
        const workspaceDoc = await getDoc(workspaceRef);

        let isShallow = false;
        let placeId = workspaceDoc.data()?.placeId;
        if (workspaceDoc.exists() && workspaceDoc.data()?.shallowConnect) {
          isShallow = true;
        }

        let accounts = [];
        if (isShallow) {
          accounts.push({
            uid: currentUID,
            wid,
            location: placeId,
            isShallow: true,
          });
        } else {
          // For regular workspaces, check for GMB connection
          const connectedAppsRef = collection(
            db,
            "users",
            currentUID,
            "fly_workspaces",
            wid,
            "connected-apps"
          );

          const gbpSnapshot = await getDocs(
            query(
              connectedAppsRef,
              where("__name__", "==", "google_business_profile")
            )
          );

          if (!gbpSnapshot.empty) {
            const gbpData = gbpSnapshot.docs[0].data();

            const accountId = gbpData.accounts?.[0]?.id || "";
            (gbpData.accounts || []).forEach((account) => {
              const locationId = account.locations?.[0]?.name || "";
              accounts.push({
                uid: currentUID,
                wid,
                location: locationId,
                account: accountId,
                isShallow: false,
              });
            });
          }
        }

        if (accounts.length === 0) {
          this.reviews = [];
          return;
        }

        const payload = {
          uid: currentUID,
          accounts,
          action: "get_gmb_reviews",
          platform: "google_business_profile",
          page: 1,
          pageSize: 20, // Get more reviews to filter for positive ones
        };

        let idToken = null;
        if (auth.currentUser) {
          idToken = await auth.currentUser.getIdToken();
        }

        const headers = {
          "Content-Type": "application/json",
        };
        if (idToken) {
          headers["Authorization"] = `Bearer ${idToken}`;
        }

        const endpoint = `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`;

        const response = await fetch(endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify({ data: payload }),
        });

        if (response.ok) {
          const result = await response.json();

          if (result.success) {
            const reviews = result?.data?.reviews || [];

            const ratingMap = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };

            this.reviews = reviews
              .map((review) => {
                return {
                  reviewId: review.reviewId,
                  displayName: review.reviewer?.displayName || "Anonymous",
                  profilePhotoUrl:
                    review.reviewer?.profilePhotoUrl ||
                    "https://www.gravatar.com/avatar/?d=mp",
                  starRating: ratingMap[review.starRating] || 0,
                  comment: review.comment || "",
                  updateTime: review.updateTime,
                };
              })
              .filter((review) => {
                // Only include positive reviews (4+ stars) with text content
                if (
                  !review.comment ||
                  review.comment.trim().length === 0
                ) {
                  return false;
                }
                if (review.starRating < 4) {
                  return false;
                }
                return true; // Include all positive reviews with text content
              })
              .sort((a, b) => new Date(b.updateTime) - new Date(a.updateTime)) // Sort by latest first
              .slice(0, 15); // Keep top 15 latest positive reviews
          }
        }
      } catch (e) {
        console.error("Error fetching reviews:", e);
        this.reviews = [];
      } finally {
        this.isReviewsLoading = false;
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    // Add pagination methods for reviews
    nextReviewPage() {
      if (this.hasNextReviewPage) {
        this.reviewsPage++;
      }
    },
    prevReviewPage() {
      if (this.hasPrevReviewPage) {
        this.reviewsPage--;
      }
    },
    updateFavicon() {
      // Skip favicon update if disabled
      if (this.disableTitleAndFavicon) {
        return;
      }

      // Only update favicon if we have a valid logo URL (not the default SVG)
      const logoUrl = this.storeLogo;
      const defaultSvg =
        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgMThjLTQuNDEgMC04LTMuNTktOC04cy41OS04IDggOCA4IDMuNTkgOCA4LTMuNTkgOHoiIGZpbGw9IiNlNWU3ZWIiLz48L3N2Zz4=";

      if (!logoUrl || logoUrl === defaultSvg) {
        return;
      }

      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll("link[rel~='icon']");
      existingFavicons.forEach((link) => {
        if (link.href !== this._originalFaviconHref) {
          link.remove();
        }
      });

      // Create new favicon link
      const link = document.createElement("link");
      link.rel = "icon";
      link.href = logoUrl;
      link.type = "image/x-icon";
      document.head.appendChild(link);
    },

    updatePageTitle() {
      // Skip title update if disabled
      if (this.disableTitleAndFavicon) {
        return;
      }

      if (this.brandName && this.brandName !== "Store Brand") {
        let newTitle = `${this.brandName}`;

        // Add location name if available
        if (
          this.storeLocation &&
          (this.storeLocation.name || this.storeLocation.storeLocation)
        ) {
          const locationName =
            this.storeLocation.name || this.storeLocation.storeLocation;
          newTitle += ` - ${locationName}`;
        }

        document.title = newTitle;
      }
    },
    scrollToAppointment() {
      const appointmentSection = document.getElementById("appointment");
      if (appointmentSection) {
        appointmentSection.scrollIntoView({ behavior: "smooth" });
      }
    },
    async submitAppointment() {
      this.isSubmitting = true;

      try {
        // Validate all fields before submission
        const fieldsToValidate = [
          "firstName",
          "lastName",
          "phone",
          "date",
          "time",
        ];
        fieldsToValidate.forEach((field) => this.validateField(field));

        if (this.appointmentForm.email) {
          this.validateField("email");
        }

        // Check if form is valid
        if (!this.isFormValid) {
          this.$snackbar?.show(
            "Please correct the errors in the form.",
            "error"
          );
          return;
        }

        // Get business email from store data or use fallback
        const businessEmail =
          this.storeLocation?.email ||
          this.workspaceData?.email ||
          "business@example.com";

        // Send email using Resend API
        const emailData = {
          from: "noreply@yourdomain.com", // Replace with your verified domain
          to: [businessEmail],
          subject: `New Appointment Request - ${this.brandName}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #333;">New Appointment Request</h2>
              <div style="background: #f9f9f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h3 style="color: #555; margin-top: 0;">Customer Details:</h3>
                <p><strong>Name:</strong> ${this.appointmentForm.firstName} ${
            this.appointmentForm.lastName
          }</p>
                <p><strong>Email:</strong> ${
                  this.appointmentForm.email || "Not provided"
                }</p>
                <p><strong>Phone:</strong> ${this.appointmentForm.phone}</p>
                <p><strong>Date:</strong> ${this.appointmentForm.date}</p>
                <p><strong>Time:</strong> ${this.appointmentForm.time}</p>
                ${
                  this.appointmentForm.message
                    ? `<p><strong>Message:</strong> ${this.appointmentForm.message}</p>`
                    : ""
                }
              </div>
              <p style="color: #666; font-size: 14px;">This appointment request was submitted from your store page.</p>
            </div>
          `,
        };

        // Send email to business
        const businessEmailResponse = await fetch(
          "https://api.resend.com/emails",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${process.env.VUE_APP_RESEND_API_KEY}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(emailData),
          }
        );

        if (!businessEmailResponse.ok) {
          throw new Error("Failed to send business notification");
        }

        // Send confirmation email to customer if email provided
        if (this.appointmentForm.email) {
          const customerEmailData = {
            from: "noreply@yourdomain.com", // Replace with your verified domain
            to: [this.appointmentForm.email],
            subject: `Appointment Confirmation - ${this.brandName}`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h2 style="color: #333;">Appointment Request Received</h2>
                <p>Dear ${this.appointmentForm.firstName},</p>
                <p>Thank you for your appointment request with <strong>${
                  this.brandName
                }</strong>.</p>
                <div style="background: #f9f9f9; padding: 20px; border-radius: 8px; margin: 20px 0;">
                  <h3 style="color: #555; margin-top: 0;">Appointment Details:</h3>
                  <p><strong>Date:</strong> ${this.appointmentForm.date}</p>
                  <p><strong>Time:</strong> ${this.appointmentForm.time}</p>
                  ${
                    this.appointmentForm.message
                      ? `<p><strong>Your Message:</strong> ${this.appointmentForm.message}</p>`
                      : ""
                  }
                </div>
                <p>We will review your request and contact you soon to confirm your appointment.</p>
                <p>If you have any questions, please don't hesitate to reach out.</p>
                <p>Best regards,<br>The ${this.brandName} Team</p>
              </div>
            `,
          };

          const customerEmailResponse = await fetch(
            "https://api.resend.com/emails",
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${process.env.VUE_APP_RESEND_API_KEY}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(customerEmailData),
            }
          );

          if (!customerEmailResponse.ok) {
            console.warn("Failed to send customer confirmation email");
          }
        }

        // Show success message
        this.$snackbar?.show(
          "Appointment booked successfully! We will contact you soon.",
          "success"
        );

        // Reset form
        this.appointmentForm = {
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          date: "",
          time: "",
          service: "",
          message: "",
        };

        // Optional: Scroll to top or show confirmation
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Error submitting appointment:", error);

        // Show user-friendly error message
        let errorMessage = "Error booking appointment. Please try again.";

        if (error.message.includes("Failed to send")) {
          errorMessage = "Error sending notification. Please try again later.";
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$snackbar?.show(errorMessage, "error");
      } finally {
        this.isSubmitting = false;
      }
    },
    callBusiness() {
      const phone = this.getGmbPhone(this.storeLocation);
      if (phone) {
        const url = `tel:${phone}`;
        window.open(url, "_system");
      }
    },
    navigateToSection(sectionId) {
      // Update URL with section slug
      const currentUrl = new URL(window.location);

      // For home section, set empty hash
      if (sectionId === "home") {
        currentUrl.hash = "";
      } else {
        currentUrl.hash = sectionId;
      }

      window.history.pushState({}, "", currentUrl);

      // Change current section to render only that section
      this.currentSection = sectionId;

      // Scroll to top of page for better UX
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    handleUrlHash() {
      // Get hash from URL
      const hash = window.location.hash.replace("#", "");

      // Check if this is a blog detail URL
      if (hash.startsWith("blog-detail/")) {
        const blogId = hash.split("/")[1].split("-")[0];
        this.loadBlogDetail(blogId);
        return;
      }

      // Validate if the section exists and has content
      const validSections = ["home"];

      if (this.showContactSection) validSections.push("contact");
      if (this.showReviewsSection) validSections.push("reviews");
      if (this.showPostsSection) validSections.push("posts");
      if (this.showProductsSection && this.availableServices.length > 0)
        validSections.push("products");
      if (this.showSocialMediaSection && this.socialMediaLinks.length > 0)
        validSections.push("social-media");
      if (this.showBlogsSection && this.blogs.length > 0)
        validSections.push("blogs");
      if (this.showGallerySection && this.galleryImages.length > 0)
        validSections.push("gallery");

      // Set current section based on URL hash or default to home
      if (hash && validSections.includes(hash)) {
        this.currentSection = hash;
      } else {
        // Empty hash or invalid hash defaults to home (shows all sections)
        this.currentSection = "home";
      }
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
    visitWebsite() {
      if (this.storeLocation && this.storeLocation.website) {
        window.open(this.storeLocation.website, "_blank");
      }
    },
    async fetchGalleryPosts() {
      this.isGalleryLoading = true;
      this.galleryPosts = [];

      console.log("📸 [fetchGalleryPosts] Starting to fetch gallery posts...");
      console.log("📸 [fetchGalleryPosts] Effective values:", {
        effectiveUserUid: this.effectiveUserUid,
        effectiveWid: this.effectiveWid,
        effectiveLocationId: this.effectiveLocationId,
      });

      try {
        if (!this.storeLocation || !this.effectiveLocationId) {
          console.log("📸 [fetchGalleryPosts] Missing required data:", {
            hasStoreLocation: !!this.storeLocation,
            effectiveLocationId: this.effectiveLocationId,
          });
          return;
        }

        // Use direct Firestore query similar to Posts.vue
        const postsCollection = collection(
          db,
          "users",
          this.effectiveUserUid,
          "fly_workspaces",
          this.effectiveLocationId,
          "social-posts"
        );

        // Query for latest published posts
        const postsQuery = query(
          postsCollection,
          where("post_status", "==", "published"),
          orderBy("created_at", "desc"),
          limit(6)
        );

        const postsSnapshot = await getDocs(postsQuery);
        console.log(
          "📸 [fetchGalleryPosts] Posts found for location:",
          postsSnapshot.docs.length
        );

        console.log(
          "📸 [fetchGalleryPosts] Location-specific posts found:",
          postsSnapshot.docs.length
        );

        const allPosts = postsSnapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));

        console.log(
          "📸 [fetchGalleryPosts] All posts before filtering:",
          allPosts.length
        );
        console.log(
          "📸 [fetchGalleryPosts] Sample post data:",
          allPosts.length > 0
            ? {
                id: allPosts[0].id,
                location_id: allPosts[0].location_id,
                effectiveLocationId: this.effectiveLocationId,
                created_at: allPosts[0].created_at,
                post_status: allPosts[0].post_status,
              }
            : "No posts found"
        );

        const fetchedPosts = allPosts.slice(0, 6); // Limit to 6 latest posts

        console.log(
          "📸 [fetchGalleryPosts] Posts after filtering:",
          fetchedPosts.length
        );

        console.log(
          "📸 [fetchGalleryPosts] Sample location post data:",
          fetchedPosts.length > 0
            ? {
                id: fetchedPosts[0].id,
                name: fetchedPosts[0].name,
                text: fetchedPosts[0].text?.substring(0, 100),
                post_status: fetchedPosts[0].post_status,
                location_id: fetchedPosts[0].location_id,
                created_at: fetchedPosts[0].created_at,
              }
            : "No location-specific posts found"
        );

        if (fetchedPosts.length > 0) {
          // Get platform data for social media icons (same as Posts.vue)
          const platformsRef = collection(db, "fly_platforms");
          const platformsSnapshot = await getDocs(platformsRef);
          const platforms = {};
          platformsSnapshot.forEach((doc) => {
            platforms[doc.id] = doc.data();
          });

          // Process posts to add platform information (same as Posts.vue)
          this.galleryPosts = fetchedPosts.map((post) => {
            let socialMediaArr = [];
            const socialMedias = post.social_media;

            function filteredApp(name) {
              const item = Object.values(platforms).filter((data) => {
                return data.alias == name;
              });
              return item;
            }

            if (socialMedias && Array.isArray(socialMedias)) {
              socialMedias.forEach((media) => {
                const filteredItem = filteredApp(media);
                if (filteredItem.length > 0) {
                  let data = {
                    name: media,
                    label: filteredItem[0]?.name,
                    logo_url: filteredItem[0]?.logo_url,
                  };
                  socialMediaArr.push(data);
                }
              });
            }

            return {
              ...post,
              social_media: socialMediaArr,
            };
          });
        } else {
          this.galleryPosts = [];
        }
      } catch (error) {
        console.error("Error fetching posts:", error);

        // Fallback: try to get posts from location data if available
        if (
          this.storeLocation?.recent_posts &&
          Array.isArray(this.storeLocation.recent_posts)
        ) {
          this.galleryPosts = this.storeLocation.recent_posts.map(
            (post, index) => ({
              ...post,
              id: post.id || `fallback_post_${index}_${Date.now()}`, // Ensure unique ID
            })
          );
        } else {
          this.galleryPosts = [];
        }
      } finally {
        this.isGalleryLoading = false;
      }
    },

    async fetchBlogs() {
      this.isBlogsLoading = true;
      this.blogs = [];

      console.log("📝 [fetchBlogs] Starting to fetch blogs...");
      console.log("📝 [fetchBlogs] Effective values:", {
        effectiveUserUid: this.effectiveUserUid,
        effectiveWid: this.effectiveWid,
        effectiveLocationId: this.effectiveLocationId,
      });

      try {
        if (!this.effectiveUserUid || !this.effectiveWid) {
          console.log("📝 [fetchBlogs] Missing required data:", {
            effectiveUserUid: this.effectiveUserUid,
            effectiveWid: this.effectiveWid,
          });
          return;
        }

        // Fetch blogs from the workspace
        const blogsRef = collection(
          db,
          "users",
          this.effectiveUserUid,
          "fly_workspaces",
          this.effectiveWid,
          "blogs"
        );

        const blogsQuery = query(
          blogsRef,
          where("status", "==", "published"),
          orderBy("publishedAt", "desc"),
          limit(9)
        );

        const blogsSnapshot = await getDocs(blogsQuery);
        console.log("📝 [fetchBlogs] Blogs found:", blogsSnapshot.docs.length);

        this.blogs = blogsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          publishedAt: doc.data().publishedAt ? new Date(doc.data().publishedAt) : null,
        }));

        console.log("📝 [fetchBlogs] Processed blogs:", this.blogs.length);
      } catch (error) {
        console.error("Error fetching blogs:", error);
        this.blogs = [];
      } finally {
        this.isBlogsLoading = false;
      }
    },

    formatBlogDate(dateString) {
      if (!dateString) return "No date";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    getBlogExcerpt(text) {
      if (!text) return "";
      
      // Strip HTML tags for excerpt
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      
      const maxLength = 150;
      if (plainText.length <= maxLength) return plainText;
      return plainText.substring(0, maxLength).trim() + "...";
    },
    
    sanitizeHtml(html) {
      if (!html) return "";
      
      // Simple HTML sanitization - allow safe tags and attributes
      const allowedTags = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'u', 'br', 'div', 'span'];
      const allowedAttributes = ['class', 'style'];
      
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Recursively sanitize the DOM tree
      const sanitizeNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.cloneNode();
        }
        
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName.toLowerCase();
          
          // If tag is not allowed, return only its text content
          if (!allowedTags.includes(tagName)) {
            return document.createTextNode(node.textContent || node.innerText || '');
          }
          
          // Create a new element with the same tag
          const newElement = document.createElement(tagName);
          
          // Copy allowed attributes
          for (let attr of node.attributes) {
            if (allowedAttributes.includes(attr.name.toLowerCase())) {
              newElement.setAttribute(attr.name, attr.value);
            }
          }
          
          // Recursively sanitize child nodes
          for (let child of node.childNodes) {
            const sanitizedChild = sanitizeNode(child);
            if (sanitizedChild) {
              newElement.appendChild(sanitizedChild);
            }
          }
          
          return newElement;
        }
        
        return null;
      };
      
      // Sanitize the entire content
      const sanitizedContent = [];
      for (let child of tempDiv.childNodes) {
        const sanitizedChild = sanitizeNode(child);
        if (sanitizedChild) {
          sanitizedContent.push(sanitizedChild);
        }
      }
      
      // Convert back to HTML string
      const resultDiv = document.createElement('div');
      sanitizedContent.forEach(node => resultDiv.appendChild(node.cloneNode(true)));
      
      return resultDiv.innerHTML;
    },
    getBlogTagColor() {
      try {
        // Check if document is available (component might not be mounted yet)
        if (typeof document === 'undefined' || !document.documentElement) {
          return 'blue';
        }
        
        // Extract color from CSS variable and convert to Vuetify color name
        const tagColor = getComputedStyle(document.documentElement).getPropertyValue('--blog-tag-color').trim();
        if (tagColor === '#3b82f6') return 'blue';
        if (tagColor === '#10b981') return 'green';
        if (tagColor === '#f97316') return 'orange';
        if (tagColor === '#8b5cf6') return 'purple';
        if (tagColor === '#6b7280') return 'grey';
        return 'blue'; // default
      } catch (error) {
        // Fallback to default color if CSS variable is not available
        return 'blue';
      }
    },

    openBlogDetail(blog) {
      // Set current blog and section
      this.currentBlog = blog;
      this.currentSection = 'blog-detail';
      
      // Update URL with hash
      const currentUrl = new URL(window.location);
      const slug = this.generateSlug(blog.title);
      currentUrl.hash = `blog-detail/${blog.id}-${slug}`;
      window.history.pushState({}, "", currentUrl);
      
      // Scroll to top for better UX
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    openProductDetail(product, index) {
      // Set current product and section
      this.currentProduct = product;
      this.currentSection = 'product-detail';
      
      // Update URL with hash
      const currentUrl = new URL(window.location);
      const slug = this.generateSlug(product.name);
      currentUrl.hash = `product-detail/${index}-${slug}`;
      window.history.pushState({}, "", currentUrl);
      
      // Scroll to top for better UX
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    formatNumber(num) {
      if (!num) return "0";
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + "M";
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + "K";
      }
      return num.toString();
    },
    formatDate(date) {
      if (!date) return "";
      const dateObj = date.toDate?.() || new Date(date);
      const now = new Date();
      const diffTime = Math.abs(now - dateObj);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) return "Today";
      if (diffDays === 2) return "Yesterday";
      if (diffDays <= 7) return `${diffDays - 1} days ago`;

      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year:
          dateObj.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    },
    toggleReadMore(postId) {
      // Create a new object to ensure reactivity
      const newExpandedPosts = { ...this.expandedPosts };
      newExpandedPosts[postId] = !newExpandedPosts[postId];
      this.expandedPosts = newExpandedPosts;
    },
    getSocialPlatformConfig(platform) {
      const platformConfigs = {
        facebook: {
          name: "Facebook",
          icon: "mdi-facebook",
          iconColor: "#1877F2",
        },
        instagram: {
          name: "Instagram",
          icon: "mdi-instagram",
          iconColor: "#E4405F",
        },
        twitter: {
          name: "Twitter",
          icon: "mdi-twitter",
          iconColor: "#1DA1F2",
        },
        youtube: {
          name: "YouTube",
          icon: "mdi-youtube",
          iconColor: "#FF0000",
        },
        threads: {
          name: "Threads",
          icon: "mdi-message-text",
          iconColor: "#000000",
        },
        linkedin: {
          name: "LinkedIn",
          icon: "mdi-linkedin",
          iconColor: "#0A66C2",
        },
        tiktok: {
          name: "TikTok",
          icon: "mdi-music-note",
          iconColor: "#000000",
        },
        whatsapp: {
          name: "WhatsApp",
          icon: "mdi-whatsapp",
          iconColor: "#25D366",
        },
      };

      // Handle both lowercase and original case
      const normalizedPlatform = platform.toLowerCase();
      return platformConfigs[normalizedPlatform] || null;
    },
    
    // Helper methods for products/services data
    getServiceImage(service) {
      // Handle new data structure (images array)
      if (service.images && Array.isArray(service.images) && service.images.length > 0) {
        // Return the first valid image URL
        for (const imageUrl of service.images) {
          if (imageUrl && imageUrl.trim() !== '') {
            return imageUrl;
          }
        }
      }
      
      // Handle old data structure (single image field)
      if (service.image && service.image.trim() !== '') {
        return service.image;
      }
      
      return null;
    },
    
    getServicePrice(service) {
      // Handle new data structure (unitPrice field)
      if (service.unitPrice && service.unitPrice.trim() !== '') {
        return service.unitPrice;
      }
      
      // Handle old data structure (price field)
      if (service.price && service.price.trim() !== '') {
        return service.price;
      }
      
      return null;
    },

    getServiceVideo(service) {
      // Handle new data structure (videos array)
      if (service.videos && Array.isArray(service.videos) && service.videos.length > 0) {
        // Return the first valid video URL
        for (const videoUrl of service.videos) {
          if (videoUrl && videoUrl.trim() !== '') {
            return videoUrl;
          }
        }
      }
      
      return null;
    },

    getServiceVideoThumbnail(service) {
      // Check if service has video metadata with thumbnail
      if (service.videoMetadata && service.videos && service.videos.length > 0) {
        const firstVideoUrl = service.videos[0];
        if (firstVideoUrl && service.videoMetadata[firstVideoUrl]) {
          return service.videoMetadata[firstVideoUrl].thumbnail_url;
        }
      }
      
      // Generate Cloudinary thumbnail URL if it's a Cloudinary video
      const videoUrl = this.getServiceVideo(service);
      if (videoUrl && videoUrl.includes('cloudinary.com')) {
        return this.generateCloudinaryThumbnailUrl(videoUrl);
      }
      
      // For YouTube videos, use YouTube thumbnail API
      if (videoUrl && this.isYouTubeUrl(videoUrl)) {
        return this.getYouTubeThumbnailUrl(videoUrl);
      }
      
      return null;
    },

    generateCloudinaryThumbnailUrl(videoUrl) {
      if (!videoUrl || !videoUrl.includes('cloudinary.com')) return null;
      
      // Extract public_id from Cloudinary URL
      const urlParts = videoUrl.split('/');
      const publicId = urlParts[urlParts.length - 1].split('.')[0];
      const cloudName = urlParts[urlParts.length - 3];
      
      return `https://res.cloudinary.com/${cloudName}/video/upload/c_thumb,w_400,h_300,g_auto/${publicId}.jpg`;
    },

    getYouTubeThumbnailUrl(videoUrl) {
      if (!videoUrl || !this.isYouTubeUrl(videoUrl)) return null;
      
      let videoId = '';
      
      // Extract video ID from different YouTube URL formats
      if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1].split('?')[0];
      } else if (videoUrl.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(videoUrl.split('?')[1]);
        videoId = urlParams.get('v');
      } else if (videoUrl.includes('youtube.com/embed/')) {
        videoId = videoUrl.split('youtube.com/embed/')[1].split('?')[0];
      }
      
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }
      
      return null;
    },

    playServiceVideo(service) {
      const videoUrl = this.getServiceVideo(service);
      if (videoUrl) {
        this.currentVideoUrl = videoUrl;
        this.videoModalVisible = true;
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
      }
    },

    truncateDescription(text, maxLength = 120) {
      if (!text || text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength).trim() + '...';
    },

    isYouTubeUrl(url) {
      if (!url) return false;
      const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
      return youtubeRegex.test(url);
    },

    getYouTubeEmbedUrl(url) {
      if (!url) return '';
      
      // Handle youtu.be URLs
      if (url.includes('youtu.be/')) {
        const videoId = url.split('youtu.be/')[1].split('?')[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      
      // Handle youtube.com URLs
      if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        const videoId = urlParams.get('v');
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }
      
      // Handle youtube.com/embed URLs (already in embed format)
      if (url.includes('youtube.com/embed/')) {
        return url;
      }
      
      return '';
    },

    openImageModal(imageUrl) {
      this.currentImageUrl = imageUrl;
      this.imageModalVisible = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    },

    openProductMediaModal(product, mediaIndex = 0) {
      this.currentProductMedia = this.getAllMedia(product);
      this.currentMediaIndex = mediaIndex;
      this.updateCurrentMedia();
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Add keyboard listeners
      this.addProductMediaKeyboardListeners();
    },

    closeImageModal() {
      this.imageModalVisible = false;
      this.currentImageUrl = '';
      // Restore body scroll
      document.body.style.overflow = '';
      // Remove keyboard listeners
      this.removeProductMediaKeyboardListeners();
    },

    getAllMedia(product) {
      const media = [];
      
      // Add images from new data structure
      if (product.images && Array.isArray(product.images)) {
        product.images.forEach((imageUrl, index) => {
          if (imageUrl && imageUrl.trim() !== '') {
            media.push({
              type: 'image',
              url: imageUrl,
              index: index
            });
          }
        });
      }
      
      // Add videos from new data structure
      if (product.videos && Array.isArray(product.videos)) {
        product.videos.forEach((videoUrl, index) => {
          if (videoUrl && videoUrl.trim() !== '') {
            media.push({
              type: 'video',
              url: videoUrl,
              index: index
            });
          }
        });
      }
      
      // Add single image from old data structure (if no images from new structure)
      if (media.length === 0 && product.image && product.image.trim() !== '') {
        media.push({
          type: 'image',
          url: product.image,
          index: 0
        });
      }
      
      return media;
    },

    openMediaModal(media) {
      if (media.type === 'image') {
        this.openImageModal(media.url);
      } else if (media.type === 'video') {
        this.openVideoModal(media.url);
      }
    },

    openProductMediaFromThumbnail(product, mediaIndex) {
      this.openProductMediaModal(product, mediaIndex);
    },

    openVideoModal(videoUrl) {
      this.currentVideoUrl = videoUrl;
      this.videoModalVisible = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    },

    closeVideoModal() {
      this.videoModalVisible = false;
      this.currentVideoUrl = '';
      // Restore body scroll
      document.body.style.overflow = '';
      // Remove keyboard listeners
      this.removeProductMediaKeyboardListeners();
    },

    nextProductMedia() {
      if (this.currentMediaIndex < this.currentProductMedia.length - 1) {
        this.currentMediaIndex++;
      } else {
        // Loop back to first media
        this.currentMediaIndex = 0;
      }
      this.updateCurrentMedia();
    },

    previousProductMedia() {
      if (this.currentMediaIndex > 0) {
        this.currentMediaIndex--;
      } else {
        // Loop to last media
        this.currentMediaIndex = this.currentProductMedia.length - 1;
      }
      this.updateCurrentMedia();
    },

    updateCurrentMedia() {
      if (this.currentProductMedia.length > 0) {
        const media = this.currentProductMedia[this.currentMediaIndex];
        if (media.type === 'image') {
          this.currentImageUrl = media.url;
          this.imageModalVisible = true;
          this.videoModalVisible = false;
        } else if (media.type === 'video') {
          this.currentVideoUrl = media.url;
          this.videoModalVisible = true;
          this.imageModalVisible = false;
        }
      }
    },

    handleProductMediaKeydown(event) {
      if (this.imageModalVisible || this.videoModalVisible) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            this.previousProductMedia();
            break;
          case 'ArrowRight':
            event.preventDefault();
            this.nextProductMedia();
            break;
          case 'Escape':
            event.preventDefault();
            this.closeImageModal();
            this.closeVideoModal();
            break;
        }
      }
    },

    addProductMediaKeyboardListeners() {
      if (!this.handleProductMediaKeydown) {
        this.handleProductMediaKeydown = this.handleProductMediaKeydown.bind(this);
        document.addEventListener('keydown', this.handleProductMediaKeydown);
      }
    },

    removeProductMediaKeyboardListeners() {
      if (this.handleProductMediaKeydown) {
        document.removeEventListener('keydown', this.handleProductMediaKeydown);
        this.handleProductMediaKeydown = null;
      }
    },

    navigateToProducts() {
      // Navigate to the products section
      this.currentSection = 'products';
      window.location.hash = '#products';
      // Scroll to the products section
      const productsSection = document.getElementById('products');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth' });
      }
    },
    
    getGmbPhone(location) {
      if (location?.gmbProfile?.phoneNumbers?.primaryPhone)
        return location.gmbProfile.phoneNumbers.primaryPhone;
      if (location?.gmbProfile?.phoneNumbers?.secondaryPhone)
        return location.gmbProfile.phoneNumbers.secondaryPhone;
      return null;
    },

    getBusinessType() {
      if (!this.storeLocation) return null;

      // Try to determine business type from various sources
      const businessName = this.brandName.toLowerCase();
      const description = (
        this.getBusinessDescription(this.storeLocation) || ""
      ).toLowerCase();
      const services = this.availableServices.map((s) => s.name.toLowerCase());

      // Common business type keywords
      const businessTypes = {
        restaurant: [
          "restaurant",
          "cafe",
          "diner",
          "bistro",
          "pizzeria",
          "grill",
          "bar",
          "pub",
        ],
        dental: ["dental", "dentist", "orthodontist", "dental clinic"],
        medical: ["medical", "clinic", "doctor", "physician", "healthcare"],
        automotive: [
          "auto",
          "car",
          "automotive",
          "mechanic",
          "repair",
          "dealership",
        ],
        beauty: ["salon", "spa", "beauty", "hair", "nail", "cosmetic"],
        fitness: ["gym", "fitness", "workout", "exercise", "training"],
        retail: ["store", "shop", "retail", "boutique", "market"],
        legal: ["law", "legal", "attorney", "lawyer", "law firm"],
        real_estate: ["real estate", "realtor", "property", "housing"],
        financial: ["bank", "financial", "credit union", "insurance"],
        education: ["school", "education", "learning", "training", "academy"],
      };

      // Check business name and description
      for (const [type, keywords] of Object.entries(businessTypes)) {
        for (const keyword of keywords) {
          if (businessName.includes(keyword) || description.includes(keyword)) {
            return type;
          }
        }
      }

      // Check services
      for (const [type, keywords] of Object.entries(businessTypes)) {
        for (const keyword of keywords) {
          if (services.some((service) => service.includes(keyword))) {
            return type;
          }
        }
      }

      return null;
    },
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },

    validateField(fieldName) {
      this.validationErrors[fieldName] = "";

      switch (fieldName) {
        case "firstName":
          if (!this.appointmentForm.firstName.trim()) {
            this.validationErrors.firstName = "First name is required";
          } else if (this.appointmentForm.firstName.trim().length < 2) {
            this.validationErrors.firstName =
              "First name must be at least 2 characters";
          }
          break;

        case "lastName":
          if (!this.appointmentForm.lastName.trim()) {
            this.validationErrors.lastName = "Last name is required";
          } else if (this.appointmentForm.lastName.trim().length < 2) {
            this.validationErrors.lastName =
              "Last name must be at least 2 characters";
          }
          break;

        case "email":
          if (
            this.appointmentForm.email &&
            !this.isValidEmail(this.appointmentForm.email)
          ) {
            this.validationErrors.email = "Please enter a valid email address";
          }
          break;

        case "phone":
          if (!this.appointmentForm.phone.trim()) {
            this.validationErrors.phone = "Phone number is required";
          } else if (!this.isValidPhone(this.appointmentForm.phone)) {
            this.validationErrors.phone = "Please enter a valid phone number";
          }
          break;

        case "date":
          if (!this.appointmentForm.date) {
            this.validationErrors.date = "Please select a date";
          } else {
            const selectedDate = new Date(this.appointmentForm.date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate < today) {
              this.validationErrors.date = "Please select a future date";
            }
          }
          break;

        case "time":
          if (!this.appointmentForm.time) {
            this.validationErrors.time = "Please select a time";
          }
          break;
      }
    },

    isValidPhone(phone) {
      // Basic phone validation - allows various formats
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      const cleanPhone = phone.replace(/[\s\-\(\)]/g, "");
      return phoneRegex.test(cleanPhone);
    },

    async enableAppointments() {
      if (!this.effectiveUserUid || !this.effectiveLocationId) {
        console.error("Missing required data to enable appointments");
        return;
      }

      this.isEnablingAppointments = true;

      try {
        // Update the location document to enable appointments
        const locationRef = doc(
          db,
          "users",
          this.effectiveUserUid,
          "fly_workspaces",
          this.effectiveLocationId
        );

        // Get current location data to preserve existing settings
        const locationDoc = await getDoc(locationRef);
        const currentData = locationDoc.exists() ? locationDoc.data() : {};

        // Update location_style to enable appointments
        const updatedLocationStyle = {
          ...currentData.location_style,
          appointments: true,
        };

        await updateDoc(locationRef, {
          location_style: updatedLocationStyle,
        });

        // Update local state to reflect the change immediately
        if (this.storeLocation) {
          this.storeLocation.location_style = updatedLocationStyle;
        }

        // Show success message
        this.$snackbar?.show("Appointments enabled successfully!", "success");

        // Scroll to the appointment section
        this.$nextTick(() => {
          this.scrollToSection("appointment");
        });
      } catch (error) {
        console.error("Error enabling appointments:", error);
        this.$snackbar?.show(
          "Error enabling appointments. Please try again.",
          "error"
        );
      } finally {
        this.isEnablingAppointments = false;
      }
    },

    updateMetaTags() {
      if (!this.storeLocation || this.isLoading) return;

      // Update page title
      if (!this.disableTitleAndFavicon) {
        document.title = this.seoPageTitle;
      }

      // Update meta description
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = this.seoMetaDescription;

      // Update keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement("meta");
        metaKeywords.name = "keywords";
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.content = this.seoKeywords;

      // Update Open Graph tags
      this.updateOpenGraphTags();

      // Update Twitter Card tags
      this.updateTwitterCardTags();

      // Update schema markup
      this.updateSchemaMarkup();
    },

    updateOpenGraphTags() {
      const ogTags = {
        "og:title": this.seoPageTitle,
        "og:description": this.seoMetaDescription,
        "og:type": "website",
        "og:url": this.currentPageUrl,
        "og:image": this.seoImageUrl,
        "og:image:width": "1200",
        "og:image:height": "630",
        "og:site_name": this.brandName,
        "og:locale": "en_US",
      };

      Object.entries(ogTags).forEach(([property, content]) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute("property", property);
          document.head.appendChild(meta);
        }
        meta.content = content;
      });
    },

    updateTwitterCardTags() {
      const twitterTags = {
        "twitter:card": "summary_large_image",
        "twitter:title": this.seoPageTitle,
        "twitter:description": this.seoMetaDescription,
        "twitter:image": this.seoImageUrl,
        "twitter:site": "@flysocial",
      };

      Object.entries(twitterTags).forEach(([name, content]) => {
        let meta = document.querySelector(`meta[name="${name}"]`);
        if (!meta) {
          meta = document.createElement("meta");
          meta.name = name;
          document.head.appendChild(meta);
        }
        meta.content = content;
      });
    },

    updateSchemaMarkup() {
      // Remove existing schema markup
      const existingSchema = document.querySelector(
        'script[type="application/ld+json"]'
      );
      if (existingSchema) {
        existingSchema.remove();
      }

      // Add new schema markup
      const schemaScript = document.createElement("script");
      schemaScript.type = "application/ld+json";
      schemaScript.textContent = this.schemaMarkup;
      document.head.appendChild(schemaScript);
    },

    // Cover photo handling methods
    handleCoverPhotoError(event) {
      console.error("Cover photo failed to load:", event.target.src);
      // Set error flag to hide the cover photo panel
      this.coverPhotoError = true;
      this.coverPhotoLoading = false;
    },

    handleCoverPhotoLoad() {
      console.log("Cover photo loaded successfully");
      this.coverPhotoError = false;
      this.coverPhotoLoading = false;
    },

    backToBlogs() {
      // Check if user came from blogs section or home
      const currentHash = window.location.hash;
      
      // If we're in blog detail and there's a previous hash, try to go back to that
      if (currentHash.includes('blog-detail')) {
        // Check if there's a previous state in history
        if (window.history.length > 1) {
          // Try to go back to previous page
          window.history.back();
          return;
        }
      }
      
      // If we can't go back or no previous page, navigate to blogs section
      this.currentBlog = null;
      this.currentSection = 'blogs';
      
      // Update URL with hash
      const currentUrl = new URL(window.location);
      currentUrl.hash = 'blogs';
      window.history.pushState({}, "", currentUrl);
      
      // Scroll to top for better UX
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    backToProducts() {
      // Check if user came from products section or home
      const currentHash = window.location.hash;
      
      // If we're in product detail and there's a previous hash, try to go back to that
      if (currentHash.includes('product-detail')) {
        // Check if there's a previous state in history
        if (window.history.length > 1) {
          // Try to go back to previous page
          window.history.back();
          return;
        }
      }
      
      // If we can't go back or no previous page, navigate to products section
      this.currentProduct = null;
      this.currentSection = 'products';
      
      // Update URL with hash
      const currentUrl = new URL(window.location);
      currentUrl.hash = 'products';
      window.history.pushState({}, "", currentUrl);
      
      // Scroll to top for better UX
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    async loadBlogDetail(blogId) {
      this.isBlogDetailLoading = true;
      this.currentSection = 'blog-detail';
      
      try {
        // Extract the actual blog ID from the URL parameter (remove slug part)
        const actualBlogId = blogId.split('-')[0];
        
        // Find the blog in the existing blogs array
        const blog = this.blogs.find(b => b.id === actualBlogId);
        
        if (blog) {
          this.currentBlog = blog;
        } else {
          // If not found in existing blogs, fetch it from Firestore
          const blogRef = doc(
            db,
            "users",
            this.effectiveUserUid,
            "fly_workspaces",
            this.effectiveWid,
            "blogs",
            actualBlogId
          );

          const blogDoc = await getDoc(blogRef);
          if (blogDoc.exists()) {
            this.currentBlog = {
              id: blogDoc.id,
              ...blogDoc.data(),
              publishedAt: blogDoc.data().publishedAt ? new Date(blogDoc.data().publishedAt) : null,
            };
          } else {
            // Blog not found, redirect to blogs section
            this.currentSection = 'blogs';
            this.currentBlog = null;
          }
        }
      } catch (error) {
        console.error("Error loading blog detail:", error);
        this.currentSection = 'blogs';
        this.currentBlog = null;
      } finally {
        this.isBlogDetailLoading = false;
      }
    },

    async loadProductDetail(productId) {
      this.isProductDetailLoading = true;
      this.currentSection = 'product-detail';
      
      try {
        // Extract the actual product index from the URL parameter (remove slug part)
        const actualProductIndex = parseInt(productId.split('-')[0]);
        
        // Find the product in the existing availableServices array
        const product = this.availableServices[actualProductIndex];
        
        if (product) {
          this.currentProduct = product;
        } else {
          // Product not found, redirect to products section
          this.currentSection = 'products';
          this.currentProduct = null;
        }
      } catch (error) {
        console.error("Error loading product detail:", error);
        this.currentSection = 'products';
        this.currentProduct = null;
      } finally {
        this.isProductDetailLoading = false;
      }
    },

    handlePopState(event) {
      // Handle browser back/forward button clicks
      const hash = window.location.hash.replace("#", "");
      
      if (hash.startsWith("blog-detail/")) {
        const blogId = hash.split("/")[1];
        this.loadBlogDetail(blogId);
      } else if (hash.startsWith("product-detail/")) {
        const productId = hash.split("/")[1];
        this.loadProductDetail(productId);
      } else if (hash === "blogs") {
        this.currentBlog = null;
        this.currentSection = "blogs";
      } else if (hash === "products") {
        this.currentProduct = null;
        this.currentSection = "products";
      } else if (hash === "" || hash === "home") {
        this.currentBlog = null;
        this.currentProduct = null;
        this.currentSection = "home";
      } else {
        // For any other hash, try to navigate to that section
        this.handleUrlHash();
      }
    },

    // Post modal methods
    openPostModal(post) {
      this.selectedPost = post;
      this.showPostModal = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    },

    closePostModal() {
      this.showPostModal = false;
      this.selectedPost = null;
      // Restore body scroll
      document.body.style.overflow = 'auto';
    },

    async fetchGalleryImages() {
      this.isGalleryLoading = true;
      this.galleryImages = [];

      console.log("🖼️ [fetchGalleryImages] Starting to fetch gallery images...");
      console.log("🖼️ [fetchGalleryImages] Effective values:", {
        effectiveUserUid: this.effectiveUserUid,
        effectiveLocationId: this.effectiveLocationId,
      });

      try {
        if (!this.effectiveUserUid || !this.effectiveLocationId) {
          console.log("🖼️ [fetchGalleryImages] Missing required data:", {
            effectiveUserUid: this.effectiveUserUid,
            effectiveLocationId: this.effectiveLocationId,
          });
          return;
        }

        // Fetch gallery images from the location's photo_gallery collection
        const galleryRef = collection(
          db,
          "users",
          this.effectiveUserUid,
          "fly_workspaces",
          this.effectiveLocationId,
          "photo_gallery"
        );

        const gallerySnapshot = await getDocs(galleryRef);
        console.log("🖼️ [fetchGalleryImages] Gallery documents found:", gallerySnapshot.docs.length);

        const allImages = [];
        
        // Iterate through each document in the photo_gallery collection
        for (const doc of gallerySnapshot.docs) {
          const data = doc.data();
          console.log("🖼️ [fetchGalleryImages] Document data:", data);
          
          // Check if the document has mediaItems array
          if (data.mediaItems && Array.isArray(data.mediaItems)) {
            // Iterate through each item in mediaItems array
            data.mediaItems.forEach((item) => {
              if (item && item.googleUrl) {
                allImages.push({
                  googleUrl: item.googleUrl,
                  thumbnailUrl: item.thumbnailUrl, // Keep original thumbnailUrl, don't fallback here
                  // Add any other properties from the item if needed
                  ...item
                });
              }
            });
          }
        }

        console.log("🖼️ [fetchGalleryImages] Total images found:", allImages.length);
        this.galleryImages = allImages;
        
      } catch (error) {
        console.error("Error fetching gallery images:", error);
        this.galleryImages = [];
      } finally {
        this.isGalleryLoading = false;
      }
    },

    // Gallery modal methods
    openGalleryModal(index) {
      this.currentGalleryIndex = index;
      this.showGalleryModal = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Add keyboard event listeners
      this.addGalleryKeyboardListeners();
    },

    closeGalleryModal() {
      this.showGalleryModal = false;
      this.currentGalleryIndex = 0;
      // Restore body scroll
      document.body.style.overflow = 'auto';
      // Remove keyboard event listeners
      this.removeGalleryKeyboardListeners();
    },

    addGalleryKeyboardListeners() {
      this.handleGalleryKeydown = (event) => {
        switch(event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            this.previousGalleryImage();
            break;
          case 'ArrowRight':
            event.preventDefault();
            this.nextGalleryImage();
            break;
          case 'Escape':
            event.preventDefault();
            this.closeGalleryModal();
            break;
        }
      };
      document.addEventListener('keydown', this.handleGalleryKeydown);
    },

    removeGalleryKeyboardListeners() {
      if (this.handleGalleryKeydown) {
        document.removeEventListener('keydown', this.handleGalleryKeydown);
        this.handleGalleryKeydown = null;
      }
    },

    nextGalleryImage() {
      if (this.currentGalleryIndex < this.galleryImages.length - 1) {
        this.currentGalleryIndex++;
      } else {
        // Loop back to first image
        this.currentGalleryIndex = 0;
      }
    },

    previousGalleryImage() {
      if (this.currentGalleryIndex > 0) {
        this.currentGalleryIndex--;
      } else {
        // Loop to last image
        this.currentGalleryIndex = this.galleryImages.length - 1;
      }
    },

    getGalleryImageUrl(image) {
      // Check if thumbnailUrl exists and is a valid URL
      if (image.thumbnailUrl && 
          image.thumbnailUrl !== image.googleUrl && 
          image.thumbnailUrl.startsWith('http')) {
        return image.thumbnailUrl;
      }
      // Fallback to googleUrl
      return image.googleUrl;
    },

    handleGalleryImageError(event, image, index) {
      console.warn('Gallery image failed to load:', image.thumbnailUrl || image.googleUrl);
      
      // If thumbnail failed, try to load the full image
      if (event.target.src === image.thumbnailUrl && image.googleUrl !== image.thumbnailUrl) {
        event.target.src = image.googleUrl;
        return; // Don't mark as error yet, try the full image first
      }
      
      // If both thumbnail and full image failed, mark as error
      this.$set(this.galleryImages, index, { ...image, loadError: true });
    },

    handleGalleryImageLoad(image, index) {
      // Mark image as successfully loaded
      if (image.loadError) {
        this.$set(this.galleryImages, index, { ...image, loadError: false });
      }
    },

    handleModalImageError() {
      // Mark current image as failed to load
      if (this.currentGalleryImage) {
        const index = this.galleryImages.findIndex(img => img === this.currentGalleryImage);
        if (index !== -1) {
          this.$set(this.galleryImages, index, { ...this.currentGalleryImage, loadError: true });
        }
      }
    },
    getGoogleMapsDirectionsUrl() {
      if (this.apifyLat && this.apifyLng) {
        return `https://www.google.com/maps?daddr=${this.apifyLat},${this.apifyLng}&travelmode=driving`;
      }
      return "";
    },
  },
  mounted() {
    // Store original favicon reference
    const favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      this._originalFaviconHref = favicon.href;
    }

    // Store original page title
    this._originalPageTitle = document.title;

    // Handle URL hash on page load
    this.handleUrlHash();

    // Add popstate event listener for browser back/forward buttons
    window.addEventListener('popstate', this.handlePopState);

    this.fetchStoreData();
  },

  updated() {
    // Update meta tags when data changes
    this.updateMetaTags();
  },
  beforeDestroy() {
    // Remove popstate event listener
    window.removeEventListener('popstate', this.handlePopState);

    // Remove gallery keyboard listeners
    this.removeGalleryKeyboardListeners();

    // Only restore if title and favicon updates were not disabled
    if (!this.disableTitleAndFavicon) {
      // Restore original favicon
      if (this._originalFaviconHref) {
        const favicon = document.querySelector("link[rel~='icon']");
        if (favicon) {
          favicon.href = this._originalFaviconHref;
        }
      }

      // Clean up any custom favicon links we created
      const customFavicons = document.querySelectorAll("link[rel~='icon']");
      customFavicons.forEach((link) => {
        if (link.href !== this._originalFaviconHref) {
          link.remove();
        }
      });

      // Restore original page title
      if (this._originalPageTitle) {
        document.title = this._originalPageTitle;
      }
    }
  },
};
</script>

<style scoped>
/* Clean, Professional Design inspired by droswalsdental.com */

/* Base Styles */
.min-h-screen {
  background: var(--store-background);
  min-height: 100vh;
}

/* Header Styles */
header {
  position: relative !important;
  background: var(--header-background-color) !important;
  border-bottom: 1px solid var(--store-border-color) !important;
  box-shadow: 0 2px 10px var(--store-shadow) !important;
}

/* Hero Section */
.bg-gradient-to-br {
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%) !important;
}

/* Card Styles */
.bg-white {
  background: var(--store-background) !important;
  border: 1px solid var(--store-border-color) !important;
  box-shadow: 0 4px 6px -1px var(--store-shadow),
    0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
}

/* Button Styles */
button {
  font-weight: 600 !important;
}

/* Form Styles */
input,
select,
textarea {
  border: 1px solid var(--store-border-color) !important;
  border-radius: 0.5rem !important;
  padding: 0.75rem 1rem !important;
  font-size: 1rem !important;
}

input:focus,
select:focus,
textarea:focus {
  outline: none !important;
  border-color: var(--store-primary) !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

/* Gradient Buttons */
.bg-gradient-to-r {
  background: linear-gradient(
    to right,
    var(--store-gradient-start),
    var(--store-gradient-end)
  ) !important;
}

/* Section Spacing */
section {
  padding: 4rem 1.5rem !important;
}

/* Typography */
h1,
h2,
h3,
h4 {
  font-weight: 700 !important;
  color: var(--primary-title-color) !important;
}

p {
  color: var(--store-text-secondary) !important;
  line-height: 1.6 !important;
}

/* Icon Styles */
.v-icon {
  filter: none !important;
}

/* Footer - Using CSS variables for dynamic theming */
footer {
  /* Background and text colors are handled by CSS variables in the template */
  color: var(--footer-text-color) !important;
}

footer h3 {
  color: var(--footer-brand-name-color) !important;
}

footer .text-sm {
  color: var(--footer-text-color) !important;
}

footer .text-xs {
  color: var(--footer-copyright-color) !important;
}

footer p {
  color: var(--footer-text-color) !important;
}

footer a {
  color: var(--footer-copyright-color) !important;
}

footer a:hover {
  color: var(--footer-text-color) !important;
}

/* Override any Tailwind text color classes in footer */
footer .text-white,
footer .text-gray-500,
footer .text-gray-600,
footer .text-gray-700,
footer .text-gray-800,
footer .text-gray-900 {
  color: var(--footer-text-color) !important;
}

footer h3.text-white,
footer h3.text-gray-500,
footer h3.text-gray-600,
footer h3.text-gray-700,
footer h3.text-gray-800,
footer h3.text-gray-900 {
  color: var(--footer-brand-name-color) !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  section {
    padding: 2rem 1rem !important;
  }

  h1 {
    font-size: 2.5rem !important;
  }

  h2 {
    font-size: 2rem !important;
  }
}

/* Smooth Scrolling */
html {
  scroll-behavior: smooth;
}

/* Loading States */
.disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

/* Review Styles */
.review-item {
  background: var(--store-background) !important;
  border: 1px solid var(--store-border-color) !important;
  border-radius: 1rem !important;
  padding: 1.5rem !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

.review-author {
  font-weight: 600 !important;
  color: var(--store-text-primary) !important;
  font-size: 0.95rem !important;
}

.review-date {
  font-size: 0.8rem !important;
  color: var(--store-text-secondary) !important;
}

.review-comment {
  color: var(--store-text-primary) !important;
  font-size: 0.95rem !important;
  line-height: 1.6 !important;
  font-style: italic !important;
}

/* Pagination Styles */
.pagination-container {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  margin-top: 2rem !important;
  padding-top: 1.5rem !important;
  border-top: 1px solid var(--store-border-color) !important;
  flex-wrap: wrap !important;
  gap: 1rem !important;
}

.pagination-info {
  font-size: 0.9rem !important;
  color: var(--store-text-secondary) !important;
  font-weight: 500 !important;
}

.pagination-page-info {
  font-size: 0.9rem !important;
  color: var(--store-text-secondary) !important;
  font-weight: 500 !important;
  padding: 0 1rem !important;
  background: #f3f4f6 !important;
  border-radius: 0.5rem !important;
  min-width: 80px !important;
  text-align: center !important;
}

/* Star Rating */
.star-icon {
  filter: none !important;
}

/* Business Hours Highlight */
.bg-blue-50 {
  background-color: #eff6ff !important;
}

.border-blue-200 {
  border-color: #bfdbfe !important;
}

/* Contact Info Icons */
.w-12.h-12 {
  width: 3rem !important;
  height: 3rem !important;
}

/* Service Cards */
.w-16.h-16 {
  width: 4rem !important;
  height: 4rem !important;
}

/* Appointment Form */
.form-group {
  margin-bottom: 1.5rem !important;
}

/* Success/Error States */
.success {
  background: #dcfce7 !important;
  color: #166534 !important;
  border: 1px solid #bbf7d0 !important;
}

.error {
  background: #fef2f2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Focus States */
button:focus,
input:focus,
select:focus,
textarea:focus {
  outline: 2px solid var(--store-primary) !important;
  outline-offset: 2px !important;
}

/* Print Styles */
@media print {
  .no-print {
    display: none !important;
  }
}

/* Appointment Button Styles */
.appointment-button {
  background: var(--appointment-button-bg-color) !important;
  color: var(--appointment-button-text-color) !important;
  border-radius: var(--appointment-button-border-radius) !important;
  border: var(--appointment-button-border-size) solid
    var(--appointment-button-border-color) !important;
  transition: all 0.2s ease-in-out !important;
}

.appointment-button:hover:not(:disabled) {
  background: var(--appointment-button-hover-bg-color) !important;
  color: var(--appointment-button-hover-text-color) !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.appointment-button:active:not(:disabled) {
  transform: translateY(0) !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

/* Gallery Styles */
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gallery-card {
  transition: all 0.3s ease-in-out;
}

.gallery-card:hover {
  transform: translateY(-4px);
}

.gallery-image {
  transition: transform 0.3s ease-in-out;
}

.gallery-image:hover {
  transform: scale(1.05);
}

/* Post Grid Styles */
.post-grid {
  align-items: start !important;
}

.post-card {
  height: fit-content !important;
  align-self: start !important;
}

.post-content {
  align-items: flex-start !important;
  justify-content: flex-start !important;
}

/* Blog Styles */
.blog-card {
  /* Removed hover animation */
}

.blog-image {
  /* Removed hover animation */
}

/* Gallery Styles */
.gallery-item {
  transition: all 0.3s ease-in-out;
}

.gallery-item:hover {
  transform: translateY(-2px);
}

.gallery-item:hover .gallery-item-container {
  box-shadow: var(--gallery-hover-shadow) !important;
}

.gallery-item img {
  transition: transform 0.3s ease-in-out;
}

.gallery-item:hover img {
  transform: scale(1.05);
}

.gallery-item:hover .gallery-overlay {
  opacity: 1 !important;
}

/* Platform Badge Colors */
.platform-badge {
  backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.7);
}

/* Hide SEO meta container */
.seo-meta-container {
  display: none;
}

/* Cover Photo Styles */
.cover-photo-container {
  position: relative;
  overflow: hidden;
  border-radius: var(--hero-cover-photo-border-radius, 1rem);
}

.cover-photo-container img {
  transition: transform 0.3s ease;
}

/* Blog Detail Styles */
.blog-detail-content {
  max-width: 100%;
}

.blog-detail-content h1 {
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 1.5rem;
}

.blog-detail-content .prose {
  font-size: 1.125rem;
  line-height: 1.75;
}

.blog-detail-content .prose p {
  margin-bottom: 1.5rem;
}

.blog-detail-content .prose h2,
.blog-detail-content .prose h3,
.blog-detail-content .prose h4 {
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.blog-detail-content .prose ul,
.blog-detail-content .prose ol {
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
}

.blog-detail-content .prose li {
  margin-bottom: 0.5rem;
}

.blog-detail-content .prose blockquote {
  border-left: 4px solid #e5e7eb;
  padding-left: 1rem;
  margin: 1.5rem 0;
  font-style: italic;
  color: #6b7280;
}

.blog-detail-content .prose code {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875em;
}

.blog-detail-content .prose pre {
  background-color: #1f2937;
  color: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  overflow-x: auto;
  margin: 1.5rem 0;
}

.blog-detail-content .prose pre code {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

.blog-detail-content .prose a {
  color: #3b82f6;
  text-decoration: underline;
}

.blog-detail-content .prose a:hover {
  color: #2563eb;
}

.blog-detail-content .prose img {
  border-radius: 0.5rem;
  margin: 1.5rem 0;
}

.blog-detail-content .prose table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
}

.blog-detail-content .prose th,
.blog-detail-content .prose td {
  border: 1px solid #e5e7eb;
  padding: 0.75rem;
  text-align: left;
}

.blog-detail-content .prose th {
  background-color: #f9fafb;
  font-weight: 600;
}

@media (max-width: 768px) {
  .blog-detail-content h1 {
    font-size: 2rem;
  }
  
  .blog-detail-content .prose {
    font-size: 1rem;
  }
}
</style>

